// FILE: assets/script.product.js
//
// ROLE:
//   Central client-side controller for the Shopify product page. Manages variant
//   selection, gallery synchronisation, price/availability/ATC UI updates,
//   store availability, quantity controls, product zoom, and the PVR (Product
//   Variants Reloaded) Master/Child product relationship.
//
//   One runtime update strategy is active (Strategy B). Strategy A (AJAX fetch)
//   was retired in Phase 5/6 and all related functions have been removed.
//
//     Strategy B - JSON-driven (current):
//       updateVariantUIFromJSON() reads the ProductData-{id} node (via
//       getNormalizedDataForSection()) and directly updates price, availability,
//       quantity, ATC button, and style description DOM elements without any
//       network fetch. Fires on every variant change.
//
//   Two parallel runtime paths are maintained throughout:
//     PVR path:     Product page is a PVR Master product (detected via
//                   ProductData-{id}.is_shsd_parent field).
//                   Variant IDs are resolved by matching PVR option value IDs
//                   (from data-option-id attributes) against the productData JSON.
//     Non-PVR path: Standard Shopify product. Variant IDs are read directly from
//                   data-variant-id attributes on option inputs.
//
// CRITICAL CONTEXT:
//   All JSON data consumed by this file is emitted server-side by:
//     snippets/product.json.liquid  --> ProductData-{sectionId}
//     sections/main-product.liquid  --> (no separate nodes since Phase 6)
//   All HTML inputs (radio/select/option) are rendered server-side by:
//     snippets/product.variants.liquid --> snippets/product.swatches.liquid
//
// PVR SUPPORT:
//   The following functions are PVR-specific (non-PVR products: they return null):
//     getPvrProductContext()               reads ProductData is_shsd_parent + option_value_map
//     getPvrVariantIdFromForm()            maps form option IDs --> variant ID
//     getPvrVariantObjectById()            finds variant object by ID in PVR data
//     getPvrOptionIdByPositionAndName()    maps option position+name --> PVR value ID
//     getPvrOptionIdsForVariant()          builds PVR option ID list for a variant
//     getFirstAvailablePvrVariantFromChange() finds best available variant on change
//     applyResolvedPvrVariantToForm()      writes resolved variant back to form UI
//     forceOptionValueInForm()             sets a single option position in form UI
//     resetPvrReloadState()                clears theme.pvr* global state flags
//   Global theme.pvr* flags thread PVR state between VariantHelper and ProductDetails:
//     theme.pvrResolvedVariantForReload    the PVR variant object resolved on change
//
// DEPENDENCIES:
//   JSON data nodes (read via getElementById):
//     ProductData-{sectionId}             (product.json.liquid --> main-product.liquid)
//   DOM elements (queried within section.el):
//     [data-product-form]                 the <form> element
//     [data-product-options]              the variants block wrapper
//     [name='id']                         hidden variant ID input
//     .selector-option                    all radio/option/checkbox inputs
//     [data-option-index]                 per-option wrappers (fieldset or div)
//     .variant_reload                     blocks replaced on variant change
//     .product-images                     gallery container (ProductGallery root)
//     .metafield-rich_text_field          collapsible style description block
//                                         ADDITION: PVR metaobject support
//   External libraries (expected on window/theme):
//     Shopify.loadFeatures()              for model-viewer-ui, video-ui, shopify-xr
//     Shopify.ModelViewerUI               3D model viewer
//     Shopify.Plyr                        video player
//     MicroModal                          modal dialog system
//     Glider()                            gallery carousel
//     theme.Helpers.debounce()            debounce utility
//     theme.moneyFormat                   Shopify money format string
//
// RISKS / LIMITATIONS:
//   - theme.pvrResolvedVariantForReload is a global flag. If two product sections
//     exist on the same page, PVR state from one could bleed into the other.
//   - The RecV() function inside ProductLoad uses localStorage ('rVP') to track
//     recently viewed products. localStorage is synchronous and may block the main
//     thread on slow devices.
//   - The '_changeVariant' listener uses [data-section-type="product_page"] to
//     locate the product section. If multiple product sections exist on one page,
//     only the first match will be updated. This is acceptable for standard PDP
//     layouts where only one product section exists.
//
// WHEN TO USE:
//   Loaded as a theme asset on every product page. Entry point is the
//   sectionEvents.forEach() loop at the bottom of the file, which instantiates
//   ProductLoad for each section with type 'product_page' or 'product-alt'.
//
// CHANGE LOG:
//   Phase 0   -- Initial audit. JSON parse error identified and fixed.
//   Phase 1   -- Architecture documentation and code path mapping.
//   Phase 2   -- ProductData unified node introduced in product.json.liquid.
//   Phase 3   -- All JS reads migrated from ProductJson/ProductJsonNormalized
//                to ProductData. getPvrProductContext() updated.
//   Phase 4   -- computeSwatchAvailability() + applySwatchAvailability() added
//                for client-side swatch availability without fetch.
//   Phase 5   -- VarHover() and VariantLoad() AJAX prefetch retired (no-ops).
//   Phase 6   -- replaceReloadableBlocks(), shouldReloadVariantBlock(),
//                VarHover(), VariantLoad() all removed. AJAX fetch path gone.
//   Addition  -- updateStyleDescriptionFromJSON() and '_changeVariant' listener
//                added to keep the collapsible style description block in sync
//                when PVR Style option changes without a page reload.
//                Fixes: .metafield-rich_text_field not updating on Style change.
//                Files also changed: snippets/product.json.liquid

// =============================================================================
// theme.LibraryLoader
// Lazy-loads external CSS and JS assets (currently only CSS link tags for Plyr
// and ModelViewerUI). Prevents duplicate loading via status tracking.
// load() is called from ProductGallery._onModelLibraryLoad() and the video
// Promise chain in ProductGallery constructor.
// =============================================================================
theme.LibraryLoader = (function() {
  var types = {
    link: 'link',
    script: 'script'
  };
  var status = {
    requested: 'requested',
    loaded: 'loaded'
  };
  var cloudCdn = 'https://cdn.shopify.com/shopifycloud/';
  var libraries = {
    plyrShopifyStyles: {
      tagId: 'plyr-shopify-styles',
      src: cloudCdn + 'plyr/v2.0/shopify-plyr.css',
      type: types.link
    },
    modelViewerUiStyles: {
      tagId: 'shopify-model-viewer-ui-styles',
      src: cloudCdn + 'model-viewer-ui/assets/v1.0/model-viewer-ui.css',
      type: types.link
    }
  };
  function load(libraryName, callback) {
    var library = libraries[libraryName];
    if (!library) return;
    if (library.status === status.requested) return;
    callback = callback || function() {};
    if (library.status === status.loaded) {
      callback();
      return;
    }
    library.status = status.requested;
    var tag;
    switch (library.type) {
      case types.script:
        tag = createScriptTag(library, callback);
        break;
      case types.link:
        tag = createLinkTag(library, callback);
        break;
    }
    tag.id = library.tagId;
    library.element = tag;
    var firstScriptTag = document.getElementsByTagName(library.type)[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }
  function createScriptTag(library, callback) {
    var tag = document.createElement('script');
    tag.src = library.src;
    tag.addEventListener('load', function() {
      library.status = status.loaded;
      callback();
    });
    return tag;
  }
  function createLinkTag(library, callback) {
    var tag = document.createElement('link');
    tag.href = library.src;
    tag.rel = 'stylesheet';
    tag.type = 'text/css';
    tag.addEventListener('load', function() {
      library.status = status.loaded;
      callback();
    });
    return tag;
  }
  return {
    load: load
  };
})();

// =============================================================================
// ES5 class helper utilities (Babel-transpiled class pattern).
// VariantHelper_classCallCheck:   ensures VariantHelper is called with 'new'.
// VariantHelper_defineProperties: defines prototype/static properties.
// VariantHelper_createClass:      attaches proto and static props to Constructor.
// These three functions are repeated per class (ProductForm, ProductZoom, etc.)
// as a Babel transpilation artefact - each class got its own copy.
// =============================================================================
function VariantHelper_classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function VariantHelper_defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }
function VariantHelper_createClass(Constructor, protoProps, staticProps) { if (protoProps) VariantHelper_defineProperties(Constructor.prototype, protoProps); if (staticProps) VariantHelper_defineProperties(Constructor, staticProps); return Constructor; }

// =============================================================================
// getPvrVariantIdFromForm(form)
//
// PVR-specific. Resolves the Shopify variant ID for the currently selected
// option combination on a PVR parent product form.
//
// Flow:
//   1. Find the section ID from the closest <section data-section-id>.
//   2. Check that #ProductJson-{sectionId} has class 'isShsdProduct' AND
//      #optionValuesData exists - if not, this is not a PVR product (return null).
//   3. Parse productData = ProductJson innerHTML ? ['product_info'].
//   4. Parse optionIdsAndNames = optionValuesData innerHTML (map: id ? name).
//   5. Call getCurrentOptionIdsFromForm() to get selected data-option-id values.
//   6. Map each option ID ? option name via optionIdsAndNames.
//   7. Find the variant in productData.variants whose options[] JSON-stringified
//      matches the selected names JSON string.
//   8. Return that variant's id, or null if no match.
//
// Called from: VariantHelper._bindEvents() handleChange() after any option change.
// Returns: variant ID integer, or null (non-PVR or no matching variant found).
// =============================================================================
function getPvrVariantIdFromForm(form) {
  const section = form.closest('section');
  if (!section) return null;

  const sectionId = section.getAttribute('data-section-id');
  // PHASE 3: Read from unified ProductData node instead of ProductJson + optionValuesData.
  // is_shsd_parent field replaces the isShsdProduct CSS class check.
  // option_value_map replaces the separate optionValuesData DOM node parse.
  const productDataNode = document.getElementById('ProductData-' + sectionId);

  if (!productDataNode) { return null; }

  let productData;
  let optionIdsAndNames;

  try {
    productData = JSON.parse(productDataNode.innerHTML);
    if (!productData.is_shsd_parent) return null;
    optionIdsAndNames = productData.option_value_map;
    productData = { variants: productData.variants };
  } catch (e) {
    return null;
  }

  const selectedOptionIds = getCurrentOptionIdsFromForm(form);

  if (!selectedOptionIds.length) {
    return null;
  }

  const selectedOptionNames = selectedOptionIds
    .map(function(id) {
      return optionIdsAndNames[id];
    })
    .filter(Boolean);

  if (!selectedOptionNames.length) {
    return null;
  }

  const selectedOptionNamesJson = JSON.stringify(selectedOptionNames);
  const productVariantsData = productData["variants"] || [];

  for (let v of productVariantsData) {
    if (JSON.stringify(v.options) === selectedOptionNamesJson) {
      return v.id;
    }
  }

  return null;
}

// =============================================================================
// getCurrentOptionIdsFromForm(form)
//
// Reads the form DOM to collect the data-option-id values of all currently
// selected option inputs (radio/checkbox: checked; select: selected option).
// Excludes inputs with data-product-url (PVR navigation inputs, not variants).
//
// Returns: string[] of option value IDs in DOM order (position 1 first).
// Example: ['12345', '12346', '12347'] for a 3-option PVR product.
//
// Called from: getPvrVariantIdFromForm(), _onVariantChange() (for varOptionsKey).
// Selector: '[data-product-options] .selector-option:not([data-product-url])'
//   This targets all radio/select inputs rendered by product.swatches.liquid
//   and product.variants.liquid that are inside the [data-product-options] wrapper.
// =============================================================================
function getCurrentOptionIdsFromForm(form) {
  const selectedOptionIds = [];

  form.querySelectorAll('[data-product-options] .selector-option:not([data-product-url])').forEach(function(el) {
    if (el.tagName === 'OPTION') {
      if (el.selected && el.dataset.optionId) {
        selectedOptionIds.push(String(el.dataset.optionId));
      }
    } else if ((el.type === 'radio' || el.type === 'checkbox') && el.checked && el.dataset.optionId) {
      selectedOptionIds.push(String(el.dataset.optionId));
    }
  });

  return selectedOptionIds;
}

// =============================================================================
// getPvrProductContext(form)
//
// Reads and parses both PVR JSON nodes from the DOM, returning a context object
// for use by other PVR functions. Returns null if not a PVR product.
//
// Returns: { section, sectionId, productJson, productInfo, optionIdsAndNames }
//   productInfo:        the product_info sub-object from ProductJson (has .variants,
//                       .options_with_values after PVR mutation in product.json.liquid)
//   optionIdsAndNames:  flat map from optionValuesData: { id ? name }
//
// Called from: VariantHelper._bindEvents(), vChangeByMediaId(), resolveVariantFromLinkedPayload()
//   ProductDetails._onVariantChange(), selectMediaByVariantId()
// =============================================================================
function getPvrProductContext(form) {
  const section = form.closest('section');
  if (!section) return null;

  const sectionId = section.getAttribute('data-section-id');
  // PHASE 3: Read from unified ProductData node instead of ProductJson + optionValuesData.
  // is_shsd_parent field replaces isShsdProduct CSS class detection.
  const productDataNode = document.getElementById('ProductData-' + sectionId);

  if (!productDataNode) { return null; }

  try {
    const productJson = JSON.parse(productDataNode.innerHTML);
    if (!productJson.is_shsd_parent) return null;
    const productInfo = { variants: productJson.variants, options_with_values: productJson.options_with_values };
    const optionIdsAndNames = productJson.option_value_map;

    return {
      section,
      sectionId,
      productJson,
      productInfo,
      optionIdsAndNames
    };
  } catch (e) {
    return null;
  }
}

// getPvrVariantObjectById(form, variantId)
// Finds and returns the full variant object from productInfo.variants by ID.
// Used to retrieve all variant fields (option1/2/3, available, etc.) after the
// variant ID has been resolved. Called from VariantHelper._bindEvents().
function getPvrVariantObjectById(form, variantId) {
  const ctx = getPvrProductContext(form);
  if (!ctx || !ctx.productInfo || !ctx.productInfo.variants) return null;

  return (ctx.productInfo.variants || []).find(function(v) {
    return String(v.id) === String(variantId);
  }) || null;
}

// getPvrOptionIdByPositionAndName(productInfo, position, name)
// Looks up a PVR option value ID by its option position (1-based) and value name.
// Example: (productInfo, 2, 'Blue') ? '12345'
// Used by getPvrOptionIdsForVariant() to build the option ID list for a variant.
function getPvrOptionIdByPositionAndName(productInfo, position, name) {
  if (!productInfo || !productInfo.options_with_values || !name) return null;

  const option = (productInfo.options_with_values || []).find(function(opt) {
    return Number(opt.position) === Number(position);
  });

  if (!option || !option.values) return null;

  for (const value of option.values) {
    if (value && value.name === name) {
      return String(value.id);
    }
  }

  return null;
}

// getPvrOptionIdsForVariant(productInfo, variant)
// Builds the ordered array of PVR option value IDs for all options of a variant.
// Example: [id_for_option1, id_for_option2, id_for_option3]
// Result stored in theme.pvrOptionIdsForReload and used to construct the fetch URL:
//   product_url + '?option_values=' + optionIds.join(',') + '&section_id=' + id
// Called from: VariantHelper._bindEvents(), vChangeByMediaId()
function getPvrOptionIdsForVariant(productInfo, variant) {
  if (!productInfo || !variant) return [];

  const optionIds = [];

  if (variant.option1) {
    optionIds.push(getPvrOptionIdByPositionAndName(productInfo, 1, variant.option1));
  }
  if (variant.option2) {
    optionIds.push(getPvrOptionIdByPositionAndName(productInfo, 2, variant.option2));
  }
  if (variant.option3) {
    optionIds.push(getPvrOptionIdByPositionAndName(productInfo, 3, variant.option3));
  }

  return optionIds.filter(Boolean);
}

// =============================================================================
// getFirstAvailablePvrVariantFromChange(form, changedTarget)
//
// PVR fallback: when getPvrVariantIdFromForm() returns null (no exact match for
// the current option combination), this function finds the best available variant
// that satisfies the option values selected so far.
//
// Strategy:
//   1. Identify which option index changed (changedTarget's [data-option-index] parent).
//   2. Collect selected values for all positions UP TO AND INCLUDING the changed index.
//   3. Find the first available variant that matches all those known positions.
//   4. Return { variant, optionIds } for the matched variant.
//
// This handles the case where a user selects a parent-level option (e.g. Style)
// that changes the available child options - the system snaps to the first
// available variant under the new Style selection.
//
// Called from: VariantHelper._bindEvents() handleChange() when getPvrVariantIdFromForm returns null.
// =============================================================================
function getFirstAvailablePvrVariantFromChange(form, changedTarget) {
  const ctx = getPvrProductContext(form);
  if (!ctx) return null;

  const { productInfo } = ctx;
  const variants = productInfo.variants || [];
  if (!variants.length) return null;

  const changedWrapper = changedTarget.closest('[data-option-index]');
  if (!changedWrapper) return null;

  const changedIndex = Number(changedWrapper.getAttribute('data-option-index'));
  const selectedNames = [];

  // Read ALL option positions so Color and Size are available for retention.
  form.querySelectorAll('[data-product-options] [data-option-index]').forEach(function(wrapper) {
    const idx = Number(wrapper.getAttribute('data-option-index'));
    let selectedValue = null;
    if (idx === changedIndex) {
      selectedValue = changedTarget.value;
    } else {
      const checked = wrapper.querySelector('.selector-option:checked');
      const select = wrapper.querySelector('select.single-option-selector');
      if (checked) selectedValue = checked.value;
      else if (select) selectedValue = select.value;
    }
    selectedNames[idx] = selectedValue;
  });

  // Pass 1: retain current Color AND current Size.
  var matchedVariant = variants.find(function(variant) {
    if (!variant.available) return false;
    if (selectedNames[0] && variant.option1 !== selectedNames[0]) return false;
    if (selectedNames[1] && variant.option2 !== selectedNames[1]) return false;
    if (selectedNames[2] && variant.option3 !== selectedNames[2]) return false;
    return true;
  });
  // Pass 2: retain current Color only (any Size).
  if (!matchedVariant) {
    matchedVariant = variants.find(function(variant) {
      if (!variant.available) return false;
      if (selectedNames[0] && variant.option1 !== selectedNames[0]) return false;
      if (selectedNames[1] && variant.option2 !== selectedNames[1]) return false;
      return true;
    });
  }
  // Pass 3: retain current Size only, any Color (independent of Pass 2).
  if (!matchedVariant) {
    matchedVariant = variants.find(function(variant) {
      if (!variant.available) return false;
      if (selectedNames[0] && variant.option1 !== selectedNames[0]) return false;
      if (selectedNames[2] && variant.option3 !== selectedNames[2]) return false;
      return true;
    });
  }
  // Pass 4: first available variant for new Style (any Color, any Size).
  if (!matchedVariant) {
    matchedVariant = variants.find(function(variant) {
      if (!variant.available) return false;
      if (selectedNames[0] && variant.option1 !== selectedNames[0]) return false;
      return true;
    });
  }
  if (!matchedVariant) return null;

  const optionIds = [];
  if (matchedVariant.option1) {
    optionIds.push(getPvrOptionIdByPositionAndName(productInfo, 1, matchedVariant.option1));
  }
  if (matchedVariant.option2) {
    optionIds.push(getPvrOptionIdByPositionAndName(productInfo, 2, matchedVariant.option2));
  }
  if (matchedVariant.option3) {
    optionIds.push(getPvrOptionIdByPositionAndName(productInfo, 3, matchedVariant.option3));
  }

  return {
    variant: matchedVariant,
    optionIds: optionIds.filter(Boolean)
  };
}

// =============================================================================
// applyResolvedPvrVariantToForm(form, variant)
//
// After a PVR variant has been resolved (from media click or gallery interaction),
// this function writes the resolved variant's option values back to the form UI:
//   - For <select>: sets select.value to the matching option
//   - For radio inputs: sets checked=true, removes checked from siblings,
//     updates .active class on .swatch-element parents
//   - Updates the hidden [name='id'] input with variant.id
//
// Called from: reLoad() inner function inside ProductDetails._onVariantChange().
// This ensures the form UI reflects the PVR-resolved variant after a section reload.
// =============================================================================
function applyResolvedPvrVariantToForm(form, variant) {
  if (!form || !variant) return;

  const optionWrappers = form.querySelectorAll('[data-product-options] [data-option-index]');
  const optionValues = [variant.option1, variant.option2, variant.option3];

  optionWrappers.forEach(function(wrapper) {
    const idx = Number(wrapper.getAttribute('data-option-index'));
    const value = optionValues[idx];
    if (!value) return;

    const select = wrapper.querySelector('select.single-option-selector');
    if (select) {
      const matchingOption = Array.from(select.options).find(function(opt) {
        return String(opt.value) === String(value);
      });
      if (matchingOption) {
        select.value = matchingOption.value;
      }
      return;
    }

    const inputs = Array.from(wrapper.querySelectorAll('.selector-option'));
    const matchingInput = inputs.find(function(input) {
      return String(input.value) === String(value);
    });

    inputs.forEach(function(input) {
      input.checked = false;
      input.removeAttribute('checked');
    });

    if (matchingInput) {
      matchingInput.checked = true;
      matchingInput.setAttribute('checked', 'checked');

      const swatchElement = matchingInput.closest('.swatch-element');
      if (swatchElement) {
        const fieldset = wrapper.matches('fieldset') ? wrapper : wrapper.querySelector('fieldset');
        if (fieldset) {
          fieldset.querySelectorAll('.swatch-element').forEach(function(el) {
            el.classList.remove('active');
          });
        } else {
          wrapper.querySelectorAll('.swatch-element').forEach(function(el) {
            el.classList.remove('active');
          });
        }
        swatchElement.classList.add('active');
      }
    }
  });

  const hiddenVariantInput = form.querySelector('[name="id"]');
  if (hiddenVariantInput) {
    hiddenVariantInput.value = variant.id;
  }
}

// =============================================================================
// forceOptionValueInForm(form, optionIndex, optionValue)
//
// Imperatively sets a single option position in the form UI to a specific value.
// Called three times in reLoad() (for option1, option2, option3) after a PVR
// variant reload to ensure each option position reflects the resolved variant.
//
// Handles both <select> and radio input types.
// Also manages .active class on .swatch-element for visual consistency.
// Called from: reLoad() inside ProductDetails._onVariantChange().
// updateOptionLabels(form)
// Updates the inline value span within each option label (e.g. "Color: <span>Black</span>").
// Walks every [data-option-index] wrapper: finds the checked radio or selected
// <option>, reads its value, and sets it as the text of the legend span (swatch/
// button options) or label span (select/dropdown options).
// Single function covers Style (label span), Color (legend span), Size (legend span).
// Called from: reLoad(), Non-PVR handleChange block.
function updateOptionLabels(form) {
  if (!form) return;
  form.querySelectorAll('[data-product-options] [data-option-index]').forEach(function(wrapper) {
    var span = wrapper.querySelector('legend span, label span');
    if (!span) return;
    var checked = wrapper.querySelector('.selector-option:checked');
    var select  = wrapper.querySelector('select.single-option-selector');
    var value = null;
    if (checked) {
      value = checked.value;
    } else if (select) {
      value = select.options[select.selectedIndex] ? select.options[select.selectedIndex].value : null;
    }
    if (value) span.textContent = value;
  });
}

// restoreSwatchLabels(form)
// The theme's Swatches() function removes the <label> element from whichever
// .swatch-element it makes active during a change event. This label is what
// the CSS tooltip system uses (.swatch-element:hover .label). Without it the
// active swatch shows no tooltip on hover.
// This function restores any missing label by reconstructing it from the
// swatch's <input> element (id -> for attribute, value -> text content).
// Called after every variant change that updates swatch active states.
function restoreSwatchLabels(form) {
  if (!form) return;
  form.querySelectorAll('[data-product-options] .swatch-element').forEach(function(swatchEl) {
    if (swatchEl.querySelector('label')) return;
    var input = swatchEl.querySelector('input.selector-option');
    if (!input) return;
    var label = document.createElement('label');
    label.setAttribute('for', input.id);
    label.className = 'error error_color swatch-label visually-hidden';
    label.textContent = input.value;
    swatchEl.insertBefore(label, input);
  });
}

// =============================================================================
function forceOptionValueInForm(form, optionIndex, optionValue) {
  if (!form || optionValue == null) return;

  const wrapper = form.querySelector('[data-product-options] [data-option-index="' + optionIndex + '"]');
  if (!wrapper) return;

  const select = wrapper.querySelector('select.single-option-selector');
  if (select) {
    const matchingOption = Array.from(select.options).find(function(opt) {
      return String(opt.value) === String(optionValue);
    });

    if (matchingOption) {
      select.value = matchingOption.value;
    }
    return;
  }

  const inputs = Array.from(wrapper.querySelectorAll('.selector-option'));
  if (!inputs.length) return;

  let matchingInput = null;

  inputs.forEach(function(input) {
    input.checked = false;
    input.removeAttribute('checked');

    const swatchElement = input.closest('.swatch-element');
    if (swatchElement) {
      swatchElement.classList.remove('active');
    }

    if (String(input.value) === String(optionValue)) {
      matchingInput = input;
    }
  });

  if (matchingInput) {
    matchingInput.checked = true;
    matchingInput.setAttribute('checked', 'checked');

    const swatchElement = matchingInput.closest('.swatch-element');
    if (swatchElement) {
      swatchElement.classList.add('active');
    }
  }
}

// =============================================================================
// resetPvrReloadState()
//
// Clears PVR state flags after a variant reload cycle completes.
// Set by VariantHelper._bindEvents() or vChangeByMediaId() when a PVR variant
// is resolved; cleared after reLoad() so the next interaction starts clean.
//
//   theme.pvrResolvedVariantForReload -- the resolved PVR variant object
//   theme.pvrOptionIdsForReload       -- removed: was for AJAX fetch URL (Phase 5/6)
//   theme.pvrForceFreshReload         -- removed: was vLoad cache bypass (Phase 5/6)
//
// Called from: reLoad() and resetPvrReloadState() at end of _onVariantChange.
// =============================================================================
function resetPvrReloadState() {
  theme.pvrResolvedVariantForReload = null;
  // pvrOptionIdsForReload and pvrForceFreshReload removed: both flags were
  // only consumed by the AJAX fetch path retired in Phase 5/6.
}

// PHASE 6: shouldReloadVariantBlock() removed.
// Was used by replaceReloadableBlocks() to filter .variant_reload blocks.
// Both functions are dead after Phase 5 retired all AJAX fetches.
// Removed to eliminate dead code.

// =============================================================================
// DATA HELPERS
// These functions operate on the unified ProductData-{sectionId} node parsed by
// ProductLoad and stored as section.data (with section.data.normalized aliased
// to the same object). They provide client-side, fetch-free variant resolution.
// =============================================================================
// getNormalizedVariantById(sectionData, variantId)
// Finds a variant object in sectionData.normalized.variants by ID string match.
// Used by updateVariantUIFromJSON() and getCurrentResolvedVariant().
function getNormalizedVariantById(sectionData, variantId) {
  if (!sectionData || !sectionData.normalized || !sectionData.normalized.variants) return null;

  return (sectionData.normalized.variants || []).find(function(variant) {
    return String(variant.id) === String(variantId);
  }) || null;
}

// getSelectedOptionNamesFromForm(form)
// Reads the form DOM to collect the currently-selected option VALUE NAMES
// (not IDs) for each option position. Works for both radio and select inputs.
// Returns: string[] indexed by option position (0-based), e.g. ['Blue', 'M'].
// Used by getNormalizedVariantBySelectedOptions(), getRetainedOrFirstAvailableVariantFromMedia(),
//   resolveVariantFromLinkedPayload(), and vChangeByMediaId().
function getSelectedOptionNamesFromForm(form) {
  const selectedNames = [];

  form.querySelectorAll('[data-product-options] [data-option-index]').forEach(function(wrapper) {
    const idx = Number(wrapper.getAttribute('data-option-index'));
    let value = null;

    const checked = wrapper.querySelector('.selector-option:checked');
    const select = wrapper.querySelector('select.single-option-selector');

    if (checked) {
      value = checked.value;
    } else if (select) {
      value = select.value;
    }

    selectedNames[idx] = value;
  });

  return selectedNames;
}

// getNormalizedVariantBySelectedOptions(sectionData, form)
// Finds the variant in sectionData.normalized.variants that matches the form's
// currently-selected option names. Partial matches are supported (null names skip check).
// Used by updateVariantUIFromJSON() and _onVariantChange() as a fallback when
// no data-variant-id is available from the focused element.
function getNormalizedVariantBySelectedOptions(sectionData, form) {
  if (!sectionData || !sectionData.normalized || !sectionData.normalized.variants) return null;

  const selectedNames = getSelectedOptionNamesFromForm(form);
  const variants = sectionData.normalized.variants || [];

  return variants.find(function(variant) {
    if (selectedNames[0] && variant.option1 !== selectedNames[0]) return false;
    if (selectedNames[1] && variant.option2 !== selectedNames[1]) return false;
    if (selectedNames[2] && variant.option3 !== selectedNames[2]) return false;
    return true;
  }) || null;
}

// getCurrentResolvedVariant(rootEl, sectionData)
// Two-stage variant resolution:
//   1. Try hidden [name='id'] input value ? getNormalizedVariantById()
//   2. Fallback: getNormalizedVariantBySelectedOptions()
// Used as the primary entry point for updateVariantUIFromJSON().
function getCurrentResolvedVariant(rootEl, sectionData) {
  const form = rootEl.querySelector('[data-product-form]');
  const hiddenVariantInput = rootEl.querySelector('[name="id"]');

  let variant = null;

  if (hiddenVariantInput && hiddenVariantInput.value) {
    variant = getNormalizedVariantById(sectionData, hiddenVariantInput.value);
  }

  if (!variant && form) {
    variant = getNormalizedVariantBySelectedOptions(sectionData, form);
  }

  return variant || null;
}

// =============================================================================
// JSON-DRIVEN UI UPDATE FUNCTIONS (Strategy B - no fetch required)
// These functions directly update the price, availability, quantity, and ATC
// button DOM elements using data from the Normalized JSON node.
// All are called by updateVariantUIFromJSON(), which is called:
//   - On page load (ProductDetails constructor)
//   - On every variant change (ProductDetails._onVariantChange)
//   - After a section block reload (reLoad() inner function)
// =============================================================================
// formatMoneyValue(cents)
// Formats an integer cents value to a localised money string.
// Uses Shopify.formatMoney if available, otherwise falls back to raw /100 division.
// theme.moneyFormat: the shop's money format string (e.g. '${{amount}}').
function formatMoneyValue(cents) {
  if (typeof cents !== 'number') return '';
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, theme.moneyFormat);
  }
  return (cents / 100).toFixed(2);
}

// updatePriceUI(container, variant, sectionData)
// Updates [data-variant-price], [data-variant-compare-at-price], [data-savings],
// [data-savings-total], and [data-unit-price] within container.
// Shows/hides compare price and savings wrapper based on variant.compare_at_price.
// container: typically .product-details-price (the 'price' block wrapper).
function updatePriceUI(container, variant, sectionData) {
  if (!container || !variant) return;

  const priceNode = container.querySelector('[data-variant-price]');
  const compareNode = container.querySelector('[data-variant-compare-at-price]');
  const savingsWrap = container.querySelector('[data-savings]');
  const savingsTotal = container.querySelector('[data-savings-total]');
  const unitPriceWrap = container.querySelector('[data-unit-price]');

  if (priceNode) {
    if (variant.price > 0) {
      priceNode.textContent = formatMoneyValue(variant.price);
    } else {
      priceNode.textContent = sectionData.text.product_free;
    }
  }

  const hasCompare = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;

  if (compareNode) {
    if (hasCompare) {
      compareNode.textContent = formatMoneyValue(variant.compare_at_price);
      compareNode.classList.remove('hidden');
    } else {
      compareNode.textContent = '';
      compareNode.classList.add('hidden');
    }
  }

  if (savingsWrap && savingsTotal) {
    if (hasCompare) {
      savingsTotal.textContent = formatMoneyValue(variant.compare_at_price - variant.price);
      savingsWrap.classList.remove('hidden');
    } else {
      savingsTotal.textContent = '';
      savingsWrap.classList.add('hidden');
    }
  }

  if (unitPriceWrap) {
    unitPriceWrap.classList.add('hidden');
  }
}

// updateAvailabilityUI(container, variant, sectionData, sectionId)
// Updates the #inventory-{sectionId} h4 element with the appropriate stock message.
// Reads data-inv attribute for the low-stock threshold.
// Message logic: sold out ? sold_out text; low stock with deny policy ? 'N left in stock';
//   low stock with continue policy ? 'N in stock'; otherwise ? 'Available'.
function updateAvailabilityUI(container, variant, sectionData, sectionId) {
  if (!container || !variant) return;

  const inventoryNode = container.querySelector('#inventory-' + sectionId);
  if (!inventoryNode) return;

  const limitRaw = inventoryNode.getAttribute('data-inv');
  const availTotal = parseInt(limitRaw || '0', 10) || 0;

  const text = sectionData.text || {};
  let message = '';
  let extra = '';

  if (variant.available) {
    if (variant.inventory_management && typeof variant.inventory_quantity === 'number' && variant.inventory_quantity > 0) {
      if (availTotal > 0 && variant.inventory_quantity < availTotal) {
        if (variant.inventory_policy === 'deny') {
          message = (text.inventory_low_deny_template || '[count] left in stock').replace('[count]', String(variant.inventory_quantity));
        } else {
          message = (text.inventory_low_continue_template || '[count] in stock').replace('[count]', String(variant.inventory_quantity));
        }
      } else {
        message = text.availability_in_stock || 'Available';
      }
    } else {
      message = text.availability_in_stock || 'Available';
    }
  } else {
    message = text.availability_sold_out || text.product_unavailable || 'Sold out';
  }

  const h4 = inventoryNode.querySelector('h4');
  if (h4) {
    h4.innerHTML = '<span>' + message + '</span>' + extra;
  }
}

// updateQuantityUI(container, variant, productInfo, sectionId)
// Updates the #updates_{sectionId} qty input and its decrement/increment buttons.
// When variant is unavailable: sets qty to 0, disables both buttons.
// When available: applies variant.quantity_rule min/max/increment constraints,
//   clamps current value to valid range, enables/disables buttons accordingly.
function updateQuantityUI(container, variant, productInfo, sectionId) {
  if (!container || !variant) return;

  const qtyInput = container.querySelector('#updates_' + sectionId);
  const decrementBtn = container.querySelector('[data-quantity-decrement]');
  const incrementBtn = container.querySelector('[data-quantity-increment]');

  if (!qtyInput) return;

  const min = variant.quantity_rule && variant.quantity_rule.min ? variant.quantity_rule.min : 1;
  const maxRule = variant.quantity_rule && variant.quantity_rule.max ? variant.quantity_rule.max : null;
  const increment = variant.quantity_rule && variant.quantity_rule.increment ? variant.quantity_rule.increment : 1;

  if (!variant.available) {
    qtyInput.value = 0;
    qtyInput.min = 0;
    qtyInput.setAttribute('step', 1);
    qtyInput.removeAttribute('max');
    qtyInput.removeAttribute('data-max');

    if (decrementBtn) decrementBtn.disabled = true;
    if (incrementBtn) incrementBtn.disabled = true;
    return;
  }

  qtyInput.min = min;
  // step attribute intentionally not set -- theme Quantity() controls move by 1.

  let effectiveMax = null;

  if (
    variant.inventory_management &&
    variant.inventory_policy !== 'continue' &&
    typeof variant.inventory_quantity === 'number' &&
    variant.inventory_quantity >= 0
  ) {
    effectiveMax = variant.inventory_quantity;
  } else if (maxRule) {
    effectiveMax = maxRule;
  }

  if (effectiveMax !== null) {
    qtyInput.max = effectiveMax;
  } else {
    qtyInput.removeAttribute('max');
  }

  let currentValue = parseInt(qtyInput.value || min, 10);
  if (Number.isNaN(currentValue) || currentValue < min) {
    currentValue = min;
  }

  if (effectiveMax !== null && currentValue > effectiveMax) {
    currentValue = effectiveMax;
  }

  qtyInput.value = currentValue;

  if (decrementBtn) decrementBtn.disabled = currentValue <= min;
  if (incrementBtn) incrementBtn.disabled = effectiveMax !== null ? currentValue >= effectiveMax : false;

  // Update the quantity label value (e.g. "Quantity: 3") to reflect current value.
  // product.labels renders an .opt-val span inside [id^="quantity-"] for the initial
  // server-rendered value. JS keeps it in sync on every updateQuantityUI call.
  var qtyLabelVal = container.querySelector('[id^="quantity-"] legend span');
  if (qtyLabelVal) qtyLabelVal.textContent = currentValue;

  // Re-evaluate button disabled states after each +/- click.
  // The theme Quantity() sets .value programmatically (does not fire "input"),
  // so we listen for "click" on each button. setTimeout(0) reads the value
  // after Quantity() has finished updating it. Guard prevents accumulation.
  function qtyButtonListener(btn) {
    if (!btn || btn.getAttribute('data-qty-listener')) return;
    btn.setAttribute('data-qty-listener', '1');
    btn.addEventListener('click', function() {
      setTimeout(function() {
        var val = parseInt(qtyInput.value, 10);
        var minV = parseInt(qtyInput.min || 1, 10);
        var maxAttr = qtyInput.getAttribute('max');
        var maxV = maxAttr ? parseInt(maxAttr, 10) : null;
        if (decrementBtn) decrementBtn.disabled = isNaN(val) || val <= minV;
        if (incrementBtn) incrementBtn.disabled = maxV !== null && !isNaN(val) && val >= maxV;
        // Keep quantity label in sync after +/- button click.
        var lbl = container.querySelector('[id^="quantity-"] legend span');
        if (lbl && !isNaN(val)) lbl.textContent = val;
      }, 0);
    });
  }
  qtyButtonListener(decrementBtn);
  qtyButtonListener(incrementBtn);
}

// updateAddToCartUI(container, variant, sectionData, productInfo, sectionId)
// Updates the #add-to-cart-{sectionId} button text and disabled state.
// Unavailable variant: disabled + 'Sold Out' text + 'outline' CSS class.
// Available variant: enabled + atc text (preorder or 'Add to Cart').
function updateAddToCartUI(container, variant, sectionData, productInfo, sectionId) {
  if (!container) return;

  const button = container.querySelector('#add-to-cart-' + sectionId);
  if (!button) return;

  const unavailableText = sectionData.text.product_unavailable;
  const availableText = sectionData.text.product_available;

  if (!variant || !variant.available) {
    button.disabled = true;
    button.classList.add('disabled');
    button.classList.add('outline');
    button.textContent = unavailableText;
    button.setAttribute('data-atc', unavailableText);
    return;
  }

  button.disabled = false;
  button.classList.remove('disabled');
  button.classList.remove('outline');
  button.textContent = availableText;
  button.setAttribute('data-atc', availableText);
}

// ==========================================================================
// PHASE 4: CLIENT-SIDE SWATCH AVAILABILITY
//
// computeSwatchAvailability(unifiedData, selectedOptions)
//
// Replicates server-side Liquid availability logic from product.swatches.liquid
// entirely client-side, using the unified ProductData variants array.
//
// Mirrors the Liquid strategy exactly:
//   Position 1 (Style): value is available if ANY variant with option1==value
//     exists and is available.
//   Position 2 (Color): value is available if ANY variant with
//     option1==selected[0] AND option2==value exists and is available.
//   Position 3 (Size):  value is available if ANY variant with
//     option1==selected[0] AND option2==selected[1] AND option3==value
//     exists and is available.
//
//   hidden rule (mirrors Liquid option_hidden):
//   Values at position > 1 that are unavailable given current higher-level
//   selections are marked hidden (not just soldout). This prevents showing
//   e.g. Sizes that do not exist at all for the selected Style+Color.
//
// Parameters:
//   unifiedData:     parsed ProductData JSON object (section.data)
//   selectedOptions: string[] of currently selected value names, 0-indexed
//                    e.g. ["Men's Hooded Long Sleeve Tee T2", "Black", "M"]
//
// Returns: Map keyed by option position (0-based), each value a Map of
//   valueName -> { available: bool, hidden: bool }
//   e.g. { 0: { "Style A": {available:true,hidden:false}, ... },
//          1: { "Blue":    {available:true,hidden:false}, ... },
//          2: { "S":       {available:true,hidden:false}, "XS": {available:false,hidden:true} } }
//
// Called from: applySwatchAvailability() via updateVariantUIFromJSON()
//              and directly from reLoad() inner function.
// ==========================================================================
function computeSwatchAvailability(unifiedData, selectedOptions) {
  var variants = (unifiedData && unifiedData.variants) ? unifiedData.variants : [];
  var optionsWithValues = (unifiedData && unifiedData.options_with_values) ? unifiedData.options_with_values : [];
  var result = {};

  optionsWithValues.forEach(function(option) {
    var pos = option.position; // 1-based, matching Liquid option.position
    var idx = pos - 1;         // 0-based index into selectedOptions[]
    var valueMap = {};

    option.values.forEach(function(valueObj) {
      var valueName = valueObj.name;
      var isAvailable = false;

      // Mirror the Liquid availability check loop from product.swatches.liquid.
      // Scan every variant and check if any available variant contains this
      // value at the correct position with the correct higher-level selections.
      variants.forEach(function(variant) {
        if (!variant.available) return;

        if (pos === 1) {
          // Position 1: any available variant with option1 == this value.
          if (variant.option1 === valueName) isAvailable = true;

        } else if (pos === 2) {
          // Position 2: option1 must match selected[0] AND option2 == this value.
          if (variant.option1 === selectedOptions[0] &&
              variant.option2 === valueName) isAvailable = true;

        } else if (pos === 3) {
          // Position 3: option1 == selected[0] AND option2 == selected[1]
          //             AND option3 == this value.
          if (variant.option1 === selectedOptions[0] &&
              variant.option2 === selectedOptions[1] &&
              variant.option3 === valueName) isAvailable = true;
        }
      });

      // hidden mirrors Liquid option_hidden: positions > 1 that are unavailable
      // given current higher-level selections are hidden entirely, not just soldout.
      var isHidden = (pos > 1 && !isAvailable);

      valueMap[valueName] = { available: isAvailable, hidden: isHidden };
    });

    result[idx] = valueMap;
  });

  return result;
}

// ==========================================================================
// PHASE 4: applySwatchAvailability(container, availabilityMap)
//
// Reads the availability map produced by computeSwatchAvailability() and
// applies or removes .soldout and .hidden CSS classes on .swatch-element
// divs in the form. Also enables/disables the corresponding radio inputs.
//
// Matches each .swatch-element to its option position and value name via:
//   - [data-option-index] on the parent fieldset/wrapper (position)
//   - data-value attribute on the .swatch-element (value name)
//
// CSS classes managed (mirror product.swatches.liquid class names)::
//   .soldout  added when available:false  (greyed out, strikethrough X shown)
//   .hidden   added when hidden:true       (removed from view entirely)
//   .soldout  removed when available:true
//   .hidden   removed when hidden:false
//   input[disabled] toggled to match soldout state
//
// The .active class is NOT touched here -- active state is managed by
// VariantHelper._bindEvents() and applyResolvedPvrVariantToForm().
//
// Parameters:
//   container:        the rootEl (section element) containing the form
//   availabilityMap:  return value of computeSwatchAvailability()
//
// Called from: updateVariantUIFromJSON() (Strategy B update path)
//              and reLoad() inner function (after section block reload).
// ==========================================================================
function applySwatchAvailability(container, availabilityMap) {
  if (!container || !availabilityMap) return;

  // Find all option position wrappers inside [data-product-options].
  // Each wrapper has [data-option-index] matching the 0-based option position.
  var optionWrappers = container.querySelectorAll(
    '[data-product-options] [data-option-index]'
  );

  optionWrappers.forEach(function(wrapper) {
    var idx = Number(wrapper.getAttribute('data-option-index'));
    var valueMap = availabilityMap[idx];
    if (!valueMap) return;

    // Find every .swatch-element in this option wrapper.
    // Each has data-value matching the option value display name.
    var swatchEls = wrapper.querySelectorAll('.swatch-element');
    swatchEls.forEach(function(swatchEl) {
      var valueName = swatchEl.getAttribute('data-value');
      if (!valueName) return;

      var state = valueMap[valueName];
      if (!state) return;

      // Apply .soldout class and disabled attribute.
      // .soldout triggers the CSS strikethrough X icon on color swatches.
      if (state.available) {
        swatchEl.classList.remove('soldout');
        var input = swatchEl.querySelector('input');
        if (input) input.disabled = false;
      } else {
        swatchEl.classList.add('soldout');
        var input = swatchEl.querySelector('input');
        if (input) input.disabled = true;
      }

      // Apply .hidden class.
      // .hidden is only set for position > 1 values that are completely
      // unavailable given the current higher-level option selections.
      // This removes the swatch from view entirely (display:none via CSS).
      if (state.hidden) {
        swatchEl.classList.add('hidden');
      } else {
        swatchEl.classList.remove('hidden');
      }
    });
  });
}

// =============================================================================
// computeGalleryColorAvailability(unifiedData, isPvr)
//
// Builds an availability map for gallery thumbnail dimming.
//
// Non-PVR: Color = option1.
//   Map key: colorName (string)
//   A Color is available if ANY variant with option1 == colorName is available.
//   Simple global check -- no Style dimension exists.
//
// PVR: Each Style is a self-contained subset with its own Colors and Sizes.
//   Black in Style-1 and Black in Style-2 are independent -- one can be
//   sold out while the other is available.
//   Map key: "style||color" composite string (option1 + "||" + option2)
//   A Style+Color pair is available if ANY variant with matching option1 AND
//   option2 is available.
//   Each thumbnail carries data-linked-option1 (Style) and data-linked-option2
//   (Color) so it can look up its own specific Style+Color composite key.
//
// Returns: plain object { key: true|false }
//   Non-PVR: { "Black": true, "Red": false, ... }
//   PVR:     { "Hoodie||Black": true, "Hoodie||Red": false, "Tee||Black": true, ... }
// =============================================================================
function computeGalleryColorAvailability(unifiedData, isPvr) {
  var variants = (unifiedData && unifiedData.variants) ? unifiedData.variants : [];
  var result = {};

  variants.forEach(function(variant) {
    if (isPvr) {
      // PVR: composite key = Style + "||" + Color (option1 + "||" + option2).
      // Keeps each Style's Color availability fully independent.
      var style = variant.option1;
      var color = variant.option2;
      if (!style || !color) return;
      var key = style + '||' + color;
      if (!result.hasOwnProperty(key)) result[key] = false;
      if (variant.available) result[key] = true;
    } else {
      // Non-PVR: simple Color key (option1).
      var colorName = variant.option1;
      if (!colorName) return;
      if (!result.hasOwnProperty(colorName)) result[colorName] = false;
      if (variant.available) result[colorName] = true;
    }
  });

  return result;
}

// applyGalleryAvailability(rootEl, unifiedData, isPvr)
//
// Dims gallery thumbnail figures and thumbnail strip items whose linked
// Color (within its Style for PVR) has no available variants.
// Also blocks click interactions on dimmed thumbnails via pointer-events.
//
// Dimming rule:
//   Non-PVR: dim if the Color (option1) has zero available variants globally.
//   PVR:     dim if the Style+Color pair (option1+option2) has zero available
//            variants. Each Style's Colors are evaluated independently --
//            Black sold-out in Style-1 does not affect Black in Style-2.
//
//   Never dim if at least one variant for that Color (or Style+Color) is
//   available, even if some sizes are sold out.
//
// Applies/removes .soldout on:
//   [data-product-gallery-figure]       -- main slideshow <figure> elements
//   [data-product-gallery-thumbnail]    -- thumbnail strip <div> elements
//
// Thumbnails with no data-linked-option1 attribute (lifestyle/non-variant
// images) are always left untouched -- undimmed and clickable.
//
// Parameters:
//   rootEl:       the section root element (this.$el)
//   unifiedData:  parsed ProductData JSON (section.data / sectionData)
//   isPvr:        true for PVR products (Color at option2), false for Non-PVR
// =============================================================================
function applyGalleryAvailability(rootEl, unifiedData, isPvr) {
  if (!rootEl || !unifiedData) return;

  var colorAvailMap = computeGalleryColorAvailability(unifiedData, isPvr);

  // Select both main gallery figures and thumbnail strip items.
  var thumbEls = rootEl.querySelectorAll(
    '[data-product-gallery-figure], [data-product-gallery-thumbnail]'
  );

  thumbEls.forEach(function(el) {
    // Both PVR and Non-PVR thumbnails carry data-linked-option1.
    // Non-PVR: option1 = Color (the map key directly).
    // PVR:     option1 = Style, option2 = Color (combined into composite key).
    var linkedOption1 = el.getAttribute('data-linked-option1');

    // No linked option1 -- lifestyle or non-variant image, leave untouched.
    if (!linkedOption1) return;

    var mapKey;
    if (isPvr) {
      var linkedOption2 = el.getAttribute('data-linked-option2');
      // PVR thumbnail must have both Style and Color to look up correctly.
      if (!linkedOption2) return;
      mapKey = linkedOption1 + '||' + linkedOption2;
    } else {
      mapKey = linkedOption1;
    }

    // Key not in map -- guard gracefully, leave untouched.
    if (!colorAvailMap.hasOwnProperty(mapKey)) return;

    var isAvailable = colorAvailMap[mapKey];

    if (isAvailable) {
      el.classList.remove('soldout');
      el.style.pointerEvents = '';
    } else {
      el.classList.add('soldout');
      el.style.pointerEvents = 'none';
    }
  });
}

// =============================================================================
// updateVariantUIFromJSON(rootEl, sectionData, variantId, sectionId)
//
// Master orchestrator for Strategy B (JSON-driven) UI updates.
// Resolves the current variant via getCurrentResolvedVariant(), then calls all
// four targeted update functions (price, availability, quantity, ATC button).
//
// Also syncs the hidden [name='id'] input if it differs from the resolved variant.
//
// Called from:
//   - ProductDetails constructor (initial page load)
//   - ProductDetails._onVariantChange() (on every variant change)
//   - reLoad() inside _onVariantChange() (after a section block reload)
// =============================================================================
function updateVariantUIFromJSON(rootEl, sectionData, variantId, sectionId) {
  const variant = getCurrentResolvedVariant(rootEl, sectionData);
  if (!variant) return;

  const hiddenVariantInput = rootEl.querySelector('[name="id"]');
  if (hiddenVariantInput && String(hiddenVariantInput.value) !== String(variant.id)) {
    hiddenVariantInput.value = variant.id;
  }

  const priceWrap = rootEl.querySelector('.product-details-price');
  const availabilityWrap = rootEl.querySelector('.variant-inventory')?.closest('.variant_reload');
  const quantityWrap = rootEl.querySelector('[data-quantity]')?.closest('.variant_reload');
  const buttonsWrap = rootEl.querySelector('#add-to-cart-' + sectionId)?.closest('.variant_reload');

  updatePriceUI(priceWrap, variant, sectionData);
  updateAvailabilityUI(availabilityWrap, variant, sectionData, sectionId);
  // PHASE 3: productInfo arg replaced with sectionData -- both functions ignore it internally.
  updateQuantityUI(quantityWrap, variant, sectionData, sectionId);
  updateAddToCartUI(buttonsWrap, variant, sectionData, sectionData, sectionId);

  // PHASE 4: Apply client-side swatch availability after every variant UI update.
  // Computes which swatches are soldout/hidden for the current selection and
  // applies/removes .soldout and .hidden CSS classes on .swatch-element divs.
  // Replaces the server-side Liquid availability computation that previously
  // required a fetch() to re-render the variants-* reload block.
  var swatchSelectedOptions = getSelectedOptionNamesFromForm(
    rootEl.querySelector('[data-product-form]')
  );
  var swatchAvailMap = computeSwatchAvailability(sectionData, swatchSelectedOptions);
  applySwatchAvailability(rootEl, swatchAvailMap);

  // Dim gallery thumbnails whose linked Color is completely sold out.
  // Uses sectionData directly (not the swatch availMap) so availability is
  // checked across all Styles, not just the currently selected Style.
  var isGalleryPvr = !!(sectionData && sectionData.is_shsd_parent);
  applyGalleryAvailability(rootEl, sectionData, isGalleryPvr);
}

// =============================================================================
// updateStyleDescriptionFromJSON(rootEl, sectionData, variantId)
// ADDITION: PVR metaobject dynamic description support.
//
// PURPOSE:
//   Solves the problem where Shopify Liquid renders the collapsible style
//   description block once on page load (server-side), but PVR switches child
//   products client-side via JavaScript without a page reload. Without this
//   function, the .metafield-rich_text_field block stays locked to the initially
//   loaded parent product's description regardless of which Style the customer
//   selects from the dropdown.
//
// HOW IT WORKS:
//   Reads the style_descriptions lookup map from sectionData (built server-side
//   in snippets/product.json.liquid). This map contains every variant ID across
//   all child products (PVR) or all variants (non-PVR), each mapped to the
//   rendered HTML of the corresponding metaobject description entry.
//   Writes that HTML string to the innerHTML of the .metafield-rich_text_field
//   element inside rootEl.
//
// PVR vs NON-PVR:
//   No branching is needed here. product.json.liquid builds style_descriptions
//   correctly for both PVR (one entry per child product, mapped to all that
//   child's variant IDs) and non-PVR (one entry for the product, mapped to all
//   variant IDs). This function simply looks up the current variant ID in
//   whichever map was built.
//
// PARAMETERS:
//   rootEl      -- the product <section> element (data-section-type="product_page")
//   sectionData -- parsed ProductData JSON object (from getNormalizedDataForSection)
//   variantId   -- the resolved variant ID string from the hidden [name='id'] input
//
// READS:
//   sectionData.style_descriptions  object keyed by variant ID string
//   (built in snippets/product.json.liquid, key "style_descriptions")
//
// WRITES:
//   innerHTML of .metafield-rich_text_field within rootEl
//   Note: the HTML written includes the outer .metafield-rich_text_field wrapper
//   div (from metafield_tag in Liquid), creating a nested div. This is
//   intentional -- the CSS rules for .metafield-rich_text_field descendants
//   apply regardless of nesting depth.
//
// CALLED FROM:
//   The '_changeVariant' document event listener immediately below.
//
// THEME CSS DEPENDENCY:
//   Relies on .metafield-rich_text_field CSS rules in Theme settings > Custom CSS:
//   text-align, list-style, li::before bullet, h5 font-size.
// =============================================================================
function updateStyleDescriptionFromJSON(rootEl, sectionData, variantId) {
  if (!rootEl || !sectionData || !sectionData.style_descriptions) return;

  var descriptions = sectionData.style_descriptions;
  var html = descriptions[String(variantId)];
  if (typeof html !== 'string') return;

  var block = rootEl.querySelector('.metafield-rich_text_field');
  if (block) {
    block.innerHTML = html;
  }
}

// =============================================================================
// '_changeVariant' document event listener
// ADDITION: PVR metaobject dynamic description support.
//
// PURPOSE:
//   Triggers updateStyleDescriptionFromJSON() on every Style option change so
//   the collapsible description block stays in sync with the selected PVR child
//   product without a page reload.
//
// EVENT SOURCE:
//   Listens for the '_changeVariant' CustomEvent dispatched by
//   VariantHelper._bindEvents() handleChange() on every variant selection change.
//   The dispatch is at: document.dispatchEvent(new CustomEvent('_changeVariant'))
//   in this file's handleChange() function (inside VariantHelper._bindEvents).
//   At the point this event fires, the hidden [name='id'] input has already been
//   updated with the resolved variant ID by getPvrVariantIdFromForm() or
//   getFirstAvailablePvrVariantFromChange().
//
// SECTION TARGETING:
//   Uses [data-section-type="product_page"] to locate the product section.
//   This is required because document.querySelector('section[data-section-id]')
//   would match the popup section first (it appears earlier in the DOM), returning
//   the wrong section ID and causing ProductData lookup to fail.
//   [data-section-type="product_page"] uniquely identifies the main product section.
//
// PVR vs NON-PVR:
//   No branching needed. The variant ID is correctly resolved before this event
//   fires for both PVR and non-PVR products by the existing VariantHelper logic.
//   updateStyleDescriptionFromJSON() handles both via the style_descriptions map.
//
// TIMING:
//   Runs synchronously in the same event cycle as the variant change.
//   No fetch, no setTimeout, no timing dependency on DOM updates.
//
// LIMITATION:
//   If multiple product sections exist on one page, only the first
//   [data-section-type="product_page"] match will be updated. This is acceptable
//   for standard PDP layouts where only one product section exists.
// =============================================================================
document.addEventListener('_changeVariant', function() {
  var section = document.querySelector('section[data-section-type="product_page"]');
  if (!section) return;

  var sectionId = section.getAttribute('data-section-id');
  var sectionData = getNormalizedDataForSection(sectionId);
  if (!sectionData) return;

  var variantInput = section.querySelector('[name="id"]');
  if (!variantInput || !variantInput.value) return;

  updateStyleDescriptionFromJSON(section, sectionData, variantInput.value);
});

// =============================================================================
// replaceReloadableBlocks(currentBlocks, incomingBlocks)
//
// Swaps the innerHTML of each .variant_reload block in the live DOM with the
// innerHTML of the matching block from the fetched HTML document.
// Uses shouldReloadVariantBlock() to skip non-reload blocks.
// Called after a successful fetch() in both VarHover() cache hits and the
// live fetch inside ProductDetails._onVariantChange().
// =============================================================================
// PHASE 6: replaceReloadableBlocks() removed.
// Was called with empty arrays after Phase 5C retired the fetch branch.
// Removed along with shouldReloadVariantBlock() (Phase 6A).
// function replaceReloadableBlocks(currentBlocks, incomingBlocks) { ... }

// =============================================================================
// class VariantHelper
//
// Binds change event listeners to the [data-product-options] container and
// dispatches 'product-variant-switch' custom events when an option changes.
// Also handles PVR variant resolution on change.
//
// Constructor:
//   - Guards against null option_type (returns early if no variant type set).
//   - Calls _bindEvents() to attach the debounced change listener.
//   - Calls _switchVariant(true) for the initial page-load state.
//   - If radio type: calls Swatches() to initialise swatch UI behaviours.
//
// _bindEvents() handleChange() flow (on any option input change):
//   1. Determine the new variant ID:
//      a. If NOT a gallery thumb interaction (theme.thumbID is falsy):
//         - For radio: read data-variant-id from the changed input.
//         - For select: read data-variant-id from the selected <option>.
//         - Call getPvrVariantIdFromForm() to try PVR resolution.
//           If found: override varID.value with PVR variant ID,
//             store resolved variant in theme.pvrResolvedVariantForReload,
//             store option IDs in theme.pvrOptionIdsForReload,
//             set theme.pvrForceFreshReload = true.
//           If not found: try getFirstAvailablePvrVariantFromChange() as fallback.
//           If fallback found: same PVR state storage as above.
//           If no fallback: resetPvrReloadState() (clear PVR state).
//   2. Dispatch '_changeVariant' CustomEvent on document (for external listeners).
//   3. Call _switchVariant() which dispatches 'product-variant-switch' on the form.
//
// Keyboard navigation: ArrowUp/Down/Left/Right inside [data-product-options]
// sets theme.time = 500ms, extending the debounce delay for keyboard users.
//
// Instantiated by: ProductForm constructor.
// =============================================================================
var VariantHelper = function () {
  function VariantHelper(theme_pJ, product, $form) {
    VariantHelper_classCallCheck(this, VariantHelper);
    var _this = this;
    this.product = product;
    this.optionsCount = this.product.options.length;
    if (theme_pJ.option_type == null) {
      return;
    }    
    this.$form = $form;
    this.theme_pJ = theme_pJ;
    this.optionsTypes = {
      select: 'select',
      radio: 'radio'
    };
    this._bindEvents();
    if (theme_pJ.variant_type === this.optionsTypes.radio) {
      this.optionsType = this.optionsTypes.radio;
    } else {
      this.optionsType = this.optionsTypes.select;
    }
    this._switchVariant(true);
    if (this.optionsType === this.optionsTypes.radio) {
      Swatches(this.$form);      
    };
  }
  VariantHelper_createClass(VariantHelper, [{
    key: "_bindEvents",
    value: function _bindEvents() {
      var _this = this;
      const dpo = _this.$form.querySelector('[data-product-options]'),
            varID = _this.$form.querySelector('[name="id"]');
      const handleChange = (o) => {
        if (!theme.thumbID) {
          if (_this.optionsType === _this.optionsTypes.radio) {
            varID.value = o.target.dataset.variantId || '';
          } else {
            const selectedOption = o.target.options[o.target.selectedIndex];
            varID.value = selectedOption.dataset.variantId || '';
          }

          const pvrVariantId = getPvrVariantIdFromForm(_this.$form);

          if (pvrVariantId) {
            varID.value = pvrVariantId;

            const resolvedVariant = getPvrVariantObjectById(_this.$form, pvrVariantId);
            const pvrContext = getPvrProductContext(_this.$form);

            theme.pvrResolvedVariantForReload = resolvedVariant;
            // pvrOptionIdsForReload removed: was used for AJAX fetch URL construction
            // (Phase 5/6 retired). Flag is no longer read anywhere.

            if (_this.optionsType === _this.optionsTypes.radio) {
              o.target.dataset.variantId = pvrVariantId;
            } else {
              const selectedOption = o.target.options[o.target.selectedIndex];
              selectedOption.dataset.variantId = pvrVariantId;
            }
          } else {
            const pvrFallback = getFirstAvailablePvrVariantFromChange(_this.$form, o.target);

            if (pvrFallback && pvrFallback.variant) {
              varID.value = pvrFallback.variant.id;
              theme.pvrResolvedVariantForReload = pvrFallback.variant;
              // pvrOptionIdsForReload removed: no longer read anywhere.

              if (_this.optionsType === _this.optionsTypes.radio) {
                o.target.dataset.variantId = pvrFallback.variant.id;
              } else {
                const selectedOption = o.target.options[o.target.selectedIndex];
                selectedOption.dataset.variantId = pvrFallback.variant.id;
              }
            } else {
              // Non-PVR: resolve the correct variant and update .active swatch state.
              // Color radios carry data-variant-id so varID.value may already be set,
              // but forceOptionValueInForm must always run to move .active correctly.
              // getNormalizedVariantBySelectedOptions requires sectionData.normalized
              // which is only set by ProductLoad, not by getNormalizedDataForSection.
              // So we resolve the variant directly from sectionData.variants instead.
              {
                const sectionElNP = _this.$form.closest('section');
                const sectionIdNP = sectionElNP ? sectionElNP.getAttribute('data-section-id') : null;
                const sectionDataNP = sectionIdNP ? getNormalizedDataForSection(sectionIdNP) : null;
                if (sectionDataNP && !sectionDataNP.is_shsd_parent && sectionDataNP.variants) {
                  const selectedNP = getSelectedOptionNamesFromForm(_this.$form);
                  const variantsNP = sectionDataNP.variants;

                  // Pass 1: exact match -- new Color + current Size.
                  var resolvedNP = variantsNP.find(function(v) {
                    if (selectedNP[0] && v.option1 !== selectedNP[0]) return false;
                    if (selectedNP[1] && v.option2 !== selectedNP[1]) return false;
                    if (selectedNP[2] && v.option3 !== selectedNP[2]) return false;
                    return v.available;
                  }) || null;

                  // Pass 2: new Color, first available Size.
                  // Handles the case where current Size is unavailable for
                  // the new Color. Without this, resolvedNP is null, forceOptionValueInForm
                  // never runs, .active is never set on either Color or Size swatch,
                  // and the tooltip on the active Color button stops working.
                  if (!resolvedNP) {
                    resolvedNP = variantsNP.find(function(v) {
                      if (selectedNP[0] && v.option1 !== selectedNP[0]) return false;
                      return v.available;
                    }) || null;
                  }

                  // Pass 3: safety net -- first available variant regardless of Color.
                  if (!resolvedNP) {
                    resolvedNP = variantsNP.find(function(v) { return v.available; }) || null;
                  }

                  if (resolvedNP) {
                    varID.value = resolvedNP.id;
                    forceOptionValueInForm(_this.$form, 0, resolvedNP.option1);
                    forceOptionValueInForm(_this.$form, 1, resolvedNP.option2);
                    forceOptionValueInForm(_this.$form, 2, resolvedNP.option3);
                    updateOptionLabels(_this.$form);
                    restoreSwatchLabels(_this.$form);
                  }
                }
              }
              resetPvrReloadState();
            }
          }
        }

        document.dispatchEvent(new CustomEvent('_changeVariant', {
          bubbles: true
        }));

        return _this._switchVariant(o);
      };
      theme.time = 0;
      dpo.addEventListener('keydown', (e) => {
        const focusedElement = document.activeElement;
        if (dpo.contains(focusedElement)) {
          switch (e.key) {
            case 'ArrowUp':
              theme.time = 500;
              break;
            case 'ArrowDown':
              theme.time = 500;
              break;
            case 'ArrowLeft':
              theme.time = 500;
              break;
            case 'ArrowRight':
              theme.time = 500;
              break;
          }
        }
      });
      const debouncedHandleChange = theme.Helpers.debounce(handleChange, theme.time);      
      dpo.addEventListener('change', debouncedHandleChange);
    }
  }, {
    key: "_switchVariant",
    value: function _switchVariant(f, force) {
      var _this = this,
          firstLoad = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false,
          product = this.product,
          variant = null;
      var isProduct = function isProduct(compareProduct) {
        return compareProduct === product;
      };
      let event = new CustomEvent('product-variant-switch', {
        detail: {
          product: product,
          variant: variant,
          firstLoad: firstLoad,
          isProduct: isProduct,
          theme_pJ: this.theme_pJ
        }
      });
      this.$form.dispatchEvent(event);
    }
  }]);
  return VariantHelper;
}();

function ProductForm_classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function ProductForm_defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }
function ProductForm_createClass(Constructor, protoProps, staticProps) { if (protoProps) ProductForm_defineProperties(Constructor.prototype, protoProps); if (staticProps) ProductForm_defineProperties(Constructor, staticProps); return Constructor; }
// =============================================================================
// class ProductForm
//
// Thin wrapper that initialises Cart(), sets up the 'product-variant-switch'
// listener, and instantiates VariantHelper.
//
// On 'product-variant-switch' event: calls _changeVariant() ? _changeUrl().
// _changeUrl(): updates the browser URL via history.replaceState() with the
//   current variant ID as a ?variant= query param (only when useHistory is true
//   and not in Shopify design mode).
//
// Instantiated by: ProductDetails constructor.
// =============================================================================
var ProductForm = function () {
  function ProductForm(el) {
    var _this = this;
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    ProductForm_classCallCheck(this, ProductForm);
    this.$el = options.container || el;
    this.$form = options.form || el;
    this.options = options;
    this.product = options.product;
    this.product.options = options.productVariants;
    this.postMessage = this.options.postMessage;
    this.isThemeEditor = Shopify && Shopify.designMode;
    this.useHistory = options.useHistory && !this.isThemeEditor && window.history && history.replaceState;
    this.events = [];
    Cart(options.productEl);
    this.events.push(
      this.$form.addEventListener('product-variant-switch', function(event, data) {
        var data = event.detail;
        _this._changeVariant(data);
      })
    )
    this.variantHelper = new VariantHelper(this.options.theme_pJ, this.options.product, this.$form);
  }
  ProductForm_createClass(ProductForm, [{
    key: "unload",
    value: function unload() {      
    }
  }, {
    key: "_changeVariant",
    value: function _changeVariant(data) {
      this._changeUrl();
    }
  }, {
    key: "_changeUrl",
    value: function _changeUrl(variant) {
      if (!this.useHistory) {
        return;
      }
      p = {
        variant: this.$form.querySelector('[name="id"]').value
      }
      params = Object.keys(p).map(function(k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(p[k])
      }).join('&');
      var url = "".concat(this.options.product.handle, "?").concat(params);
      history.replaceState({}, 'variant', url);
    }
  }]);
  return ProductForm;
}();

function ProductZoom_classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function ProductZoom_defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }
function ProductZoom_createClass(Constructor, protoProps, staticProps) { if (protoProps) ProductZoom_defineProperties(Constructor.prototype, protoProps); if (staticProps) ProductZoom_defineProperties(Constructor, staticProps); return Constructor; }
// =============================================================================
// class ProductZoom
//
// Adds click-to-zoom and pan behaviour to a product image figure element.
// Zoom is only enabled when the image's natural width is >= 1.25 - the container.
// Zoom uses CSS transform: scale() + translate() for smooth panning.
// Mobile: touch-based pan via ontouchmove. Desktop: mouse-based via onmousemove.
// Resets on mouseout (desktop) or ontouchend/ontouchcancel (mobile).
//
// Instantiated by: ProductGallery._initZoom() when a figure is selected and
//   zoom_enable is true (from section.settings.zoom_enable via product.json.liquid).
// =============================================================================
var ProductZoom = function () {
  function ProductZoom(el) {
    var _this = this;
    ProductZoom_classCallCheck(this, ProductZoom);
    this.el = el;
    this.$el = el;
    this.$img = this.$el.querySelector('img');
    this.$modal = document.getElementById('quick-modal');
    this.scale = this.$img.getAttribute('width') / this.$el.getBoundingClientRect().width;
    this.zoomable = this.scale >= 1.25;
    this.framerate = 60;
    this.zoomed = false;
    this.resetZoom = this._resetZoom.bind(this);
    this._init();
    this.$img.addEventListener('img:load', _this._init());
  }
  ProductZoom_createClass(ProductZoom, [{
    key: "_init",
    value: function _init() {
      this.disableZoom();
      if (this.zoomable) {
        this.enableZoom();        
      }
    }
  }, {
    key: "enableZoom",
    value: function enableZoom() {
      this.$el.classList.add('product-image-zoomable');
      this._bindEvents();
    }
  }, {
    key: "disableZoom",
    value: function disableZoom() {
      this.$el.classList.remove('product-image-zoomable');
      this._resetZoom();
    }
  }, {
    key: "_bindEvents",
    value: function _bindEvents() {
      var _this2 = this;
      this.$el.onclick = function(event){
        _this2._toggleZoom(event.clientX, event.clientY);
      };
      window.onresize = function(event){
        this.resetZoom
      };
    }
  }, {
    key: "_toggleZoom",
    value: function _toggleZoom(clientX, clientY) {
      var _this3 = this;
      if (!theme.mobile && theme.dragging) return;
      if (!this.zoomed) {
        this.$img.srcset = this.$img.getAttribute('data-zoom');        
        if (theme.detectmob) {
          this.$el.ontouchcancel = function(){
            _this3._resetZoom();
            return
          };
          document.body.classList.add('zoom-enabled');
          if (this.$el.closest('.glider')) {
            this.$el.closest('.glider').classList.add('no-drag');
          };
          this.$el.ontouchmove = theme.Helpers.debounce(function (event) {
            var touch = event.touches[0];
            _this3._positionZoom(touch.clientX, touch.clientY);
          }, 100 / this.framerate, true, true);
        } else {
          this.$el.onmouseout = function(){
            _this3._resetZoom();
            return
          };
          this.$el.onmousemove = theme.Helpers.debounce(function (event) {
            _this3._positionZoom(event.clientX, event.clientY);
          }, 100 / this.framerate, true, true);        
        }          
        this.$el.classList.add('product-image-zoomed');          
        this.$img.style.transform = 'scale(' + this.scale + ')';
        this._positionZoom(clientX, clientY);
        this.zoomed = true;
      } else {
        this._resetZoom();
      }
    }
  }, {
    key: "_resetZoom",
    value: function _resetZoom() {
      this.$el.ontouchcancel = null;
      this.$el.ontouchend = null;
      this.$el.ontouchmove = null;
      this.$el.onmousemove = null;
      this.$el.onmouseout = null;
      if (theme.detectmob) {
        document.body.classList.remove('zoom-enabled');
        if (this.$el.closest('.glider')) {
          this.$el.closest('.glider').classList.remove('no-drag');
        };
      }
      this.$el.classList.remove('product-image-zoomed');
      this.$img.style.removeProperty('transform');
      this.$modal.querySelector('.modal-overlay').classList.remove('no-scroll');
      this.zoomed = false;
    }
  }, {
    key: "_positionZoom",
    value: function _positionZoom(clientX, clientY) {
      if (!this.zoomed) {
        return;
      }
      var _this4 = this,
          figRect = this.$el.getBoundingClientRect(),
          imgRect = this.$img.getBoundingClientRect(),
          figHalfWidth = figRect.width / 2,
          figHalfHeight = figRect.height / 2,
          centerX = figRect.left + figHalfWidth,
          centerY = figRect.top + figHalfHeight,
          widthDiff = imgRect.width / 2 - figHalfWidth,
          heightDiff = imgRect.height / 2 - figHalfHeight,
          translateX = (centerX - clientX) / figHalfWidth * widthDiff / this.scale,
          translateY = (centerY - clientY) / figHalfHeight * heightDiff / this.scale;
      this.$img.style.transform = "scale(" .concat(this.scale, ") translate(").concat(translateX, "px, ").concat(translateY, "px)");
      if (theme.detectmob) {
        this.$modal.querySelector('.modal-overlay').classList.add('no-scroll');
        this.$el.ontouchend = function(){
          _this4._resetZoom();
        };
      };
    }
  }]);
  return ProductZoom;
}();

function ProductGallery_classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function ProductGallery_defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }
function ProductGallery_createClass(Constructor, protoProps, staticProps) { if (protoProps) ProductGallery_defineProperties(Constructor.prototype, protoProps); if (staticProps) ProductGallery_defineProperties(Constructor, staticProps); return Constructor; }

// =============================================================================
// PRODUCT GALLERY HELPER FUNCTIONS
// These standalone functions support ProductGallery's variant?media sync logic.
// =============================================================================
// getVariantMediaId(variant)
// Extracts the media ID from a variant object, checking featured_media first,
// then featured_image (legacy API fallback). Returns null if neither exists.
function getVariantMediaId(variant) {
  if (!variant) return null;

  if (variant.featured_media && variant.featured_media.id) {
    return String(variant.featured_media.id);
  }

  if (variant.featured_image && variant.featured_image.id) {
    return String(variant.featured_image.id);
  }

  if (variant.featured_media_id) {
    return String(variant.featured_media_id);
  }

  return null;
}

// getBestMatchingVariantForMediaId(variants, mediaId, currentSelectedOptions)
// When multiple variants share the same featured media, scores each by how well
// its option values match the currently selected options (option1=4pts, option2=2pts,
// option3=1pt) and returns the highest-scoring match.
// Used by gallery thumbnail click handling to preserve user's size selection
// when switching colors/styles via image click.
function getBestMatchingVariantForMediaId(variants, mediaId, currentSelectedOptions) {
  if (!variants || !variants.length || !mediaId) return null;

  const matches = variants.filter(function(variant) {
    return String(getVariantMediaId(variant)) === String(mediaId);
  });

  if (!matches.length) return null;
  if (matches.length === 1) return matches[0];

  let best = matches[0];
  let bestScore = -1;

  matches.forEach(function(variant) {
    let score = 0;

    if (currentSelectedOptions[0] && variant.option1 === currentSelectedOptions[0]) score += 4;
    if (currentSelectedOptions[1] && variant.option2 === currentSelectedOptions[1]) score += 2;
    if (currentSelectedOptions[2] && variant.option3 === currentSelectedOptions[2]) score += 1;

    if (score > bestScore) {
      best = variant;
      bestScore = score;
    }
  });

  return best;
}

// getNormalizedDataForSection(sectionId)
// Reads and parses the ProductData-{sectionId} DOM node (unified node, Phase 2+).
// Returns the parsed data object, or null on parse error / missing node.
// Note: the returned object does not have .normalized set -- that alias is only
// added by ProductLoad. Callers that need .normalized must use section.data directly.
// Called from: getNormalizedDataFromGallery(), selectMediaByVariantId(),
//   syncMediaToCurrentVariant(), and the Non-PVR swatch resolution block.
function getNormalizedDataForSection(sectionId) {
  // PHASE 3: Read from unified ProductData node instead of ProductJsonNormalized.
  const node = document.getElementById('ProductData-' + sectionId);
  if (!node) return null;

  try {
    return JSON.parse(node.textContent || node.innerHTML);
  } catch (e) {
    return null;
  }
}

// getMediaMappedVariants(normalizedData, mediaId)
// Looks up the media_variant_map in normalizedData for a given media ID.
// Returns the array of variant objects associated with that media item.
// Built server-side by product.json.liquid's media_variant_map capture block.
function getMediaMappedVariants(normalizedData, mediaId) {
  if (!normalizedData || !normalizedData.media_variant_map || !mediaId) {
    return [];
  }

  return normalizedData.media_variant_map[String(mediaId)] || [];
}

// getNormalizedDataFromGallery(galleryEl)
// Walks up from the gallery element to the parent <section> to get sectionId,
// then calls getNormalizedDataForSection(). Used within gallery event handlers
// that don't have direct access to the section data object.
function getNormalizedDataFromGallery(galleryEl) {
  const section = galleryEl.closest('section');
  if (!section) return null;

  const sectionId = section.getAttribute('data-section-id');
  if (!sectionId) return null;

  return getNormalizedDataForSection(sectionId);
}

// getRetainedOrFirstAvailableVariantFromMedia(mappedVariants, currentSelectedOptions, form)
// When a gallery image is clicked, tries to retain the user's current Size by finding
// an available variant matching the new media AND the current size.
// Falls back to first available, then first variant (regardless of availability).
// This implements the 'size retention' UX pattern: clicking a different color
// thumbnail keeps the same size selected if possible.
//
// Option layout differs between PVR and Non-PVR:
//   PVR:     option1=Style, option2=Color, option3=Size
//   Non-PVR: option1=Color, option2=Size  (no option3)
// The form parameter is used to detect which layout applies via getPvrProductContext().
function getRetainedOrFirstAvailableVariantFromMedia(mappedVariants, currentSelectedOptions, form) {
  if (!mappedVariants || !mappedVariants.length) return null;

  // Detect PVR vs Non-PVR to read the correct option positions.
  const isPvr = !!(form && getPvrProductContext(form));

  // PVR:     Color=currentSelectedOptions[1], Size=currentSelectedOptions[2]
  // Non-PVR: Color=currentSelectedOptions[0], Size=currentSelectedOptions[1]
  const currentColor = isPvr ? (currentSelectedOptions[1] || null) : (currentSelectedOptions[0] || null);
  const currentSize  = isPvr ? (currentSelectedOptions[2] || null) : (currentSelectedOptions[1] || null);
  const colorField   = isPvr ? 'option2' : 'option1';
  const sizeField    = isPvr ? 'option3' : 'option2';

  // Step 1: retain current Color AND current Size.
  if (currentColor && currentSize) {
    const retainBoth = mappedVariants.find(function(v) {
      return v.available &&
             String(v[colorField]) === String(currentColor) &&
             String(v[sizeField])  === String(currentSize);
    });
    if (retainBoth) return retainBoth;
  }

  // Step 2: retain current Color, first available Size.
  if (currentColor) {
    const retainColor = mappedVariants.find(function(v) {
      return v.available && String(v[colorField]) === String(currentColor);
    });
    if (retainColor) return retainColor;
  }

  // Step 3: image Color, retain current Size.
  if (currentSize) {
    const retainSize = mappedVariants.find(function(v) {
      return v.available && String(v[sizeField]) === String(currentSize);
    });
    if (retainSize) return retainSize;
  }

  // Step 4: first available (image Color, first Size).
  const firstAvailable = mappedVariants.find(function(v) { return v.available; });
  if (firstAvailable) return firstAvailable;

  return mappedVariants[0] || null;
}

// getLiveProductFormFromGallery(galleryEl)     ? finds [data-product-form] in section
// getLiveProductOptionsContainerFromGallery()  ? finds [data-product-options] in form
// getLiveSelectorOptionsFromGallery()          ? finds all .selector-option inputs
// These three helpers navigate from a gallery element up to the section root and
// then down into the form, to bridge the gallery?form interaction without storing
// explicit references.
function getLiveProductFormFromGallery(galleryEl) {
  const section = galleryEl.closest('section');
  if (!section) return null;
  return section.querySelector('[data-product-form]');
}

function getLiveProductOptionsContainerFromGallery(galleryEl) {
  const form = getLiveProductFormFromGallery(galleryEl);
  if (!form) return null;
  return form.querySelector('[data-product-options]');
}

function getLiveSelectorOptionsFromGallery(galleryEl) {
  const form = getLiveProductFormFromGallery(galleryEl);
  if (!form) return [];
  return Array.from(form.querySelectorAll('.selector-option:not([data-product-url])'));
}

// getLinkedVariantPayloadFromElement(el)
// Reads data-linked-variant-id, data-linked-option1/2/3 from a thumbnail element.
// Returns a payload object { id, option1, option2, option3 } or null.
// These attributes are set on thumbnail elements for PVR cross-product linking.
function getLinkedVariantPayloadFromElement(el) {
  if (!el) return null;

  const variantId = el.getAttribute('data-linked-variant-id');
  const option1 = el.getAttribute('data-linked-option1');
  const option2 = el.getAttribute('data-linked-option2');
  const option3 = el.getAttribute('data-linked-option3');

  if (!variantId && !option1 && !option2 && !option3) {
    return null;
  }

  return {
    id: variantId,
    option1: option1,
    option2: option2,
    option3: option3
  };
}

// =============================================================================
// resolveVariantFromLinkedPayload(form, linkedPayload, normalizedData)
//
// When a thumbnail carries data-linked-* attributes, resolves the best variant
// to select while retaining the user's current Size selection.
//
// Handles both PVR and Non-PVR option layouts:
//   PVR:     option1=Style, option2=Color, option3=Size
//            -- filters on Style+Color, retains size from option3
//   Non-PVR: option1=Color, option2=Size
//            -- filters on Color only, retains size from option2
//
//   1. Uses PVR productInfo.variants if available (getPvrProductContext).
//   2. Otherwise uses normalizedData.variants.
//   3. Filters to Color variants (PVR: Style+Color match; Non-PVR: Color match only).
//   4. Within that subset, tries to retain the user's current Size.
//   5. Falls back to first available, then first in set.
//   6. If no Color match, tries exact variant ID match.
// =============================================================================
function resolveVariantFromLinkedPayload(form, linkedPayload, normalizedData) {
  if (!form || !linkedPayload) return null;

  const pvrContext = getPvrProductContext(form);

  const variants = (
    pvrContext &&
    pvrContext.productInfo &&
    pvrContext.productInfo.variants &&
    pvrContext.productInfo.variants.length
  )
    ? pvrContext.productInfo.variants
    : ((normalizedData && normalizedData.variants) ? normalizedData.variants : []);

  if (!variants.length) return null;

  const currentSelectedOptions = getSelectedOptionNamesFromForm(form);

  // Option layout differs between PVR and Non-PVR:
  //   PVR:     option1=Style, option2=Color, option3=Size  (3 options, size at index 2)
  //   Non-PVR: option1=Color, option2=Size                 (2 options, size at index 1)
  // Detect by whether a 3rd option wrapper exists in the form.
  const isPvr = !!pvrContext;
  const retainedSize = isPvr
    ? (currentSelectedOptions[2] || null)   // PVR: Size is option3 (form index 2)
    : (currentSelectedOptions[1] || null);  // Non-PVR: Size is option2 (form index 1)

  // For Non-PVR the linked thumbnail only carries option1 (Color) + option2 (Size).
  // We filter only on the Color option (option1 for both PVR and Non-PVR) to get all
  // size variants for this Color, then apply size retention within that set.
  // For PVR we also match option2 (Color within a Style) as before.
  const sameColorVariants = isPvr
    ? variants.filter(function(variant) {
        return String(variant.option1 || '') === String(linkedPayload.option1 || '') &&
               String(variant.option2 || '') === String(linkedPayload.option2 || '');
      })
    : variants.filter(function(variant) {
        return String(variant.option1 || '') === String(linkedPayload.option1 || '');
      });

  if (!sameColorVariants.length) {
    if (linkedPayload.id) {
      return variants.find(function(variant) {
        return String(variant.id) === String(linkedPayload.id);
      }) || null;
    }
    return null;
  }

  // Try to retain the user's current Size first.
  if (retainedSize) {
    const sizeField = isPvr ? 'option3' : 'option2';
    const retained = sameColorVariants.find(function(variant) {
      return variant.available && String(variant[sizeField] || '') === String(retainedSize);
    });
    if (retained) return retained;
  }

  const firstAvailable = sameColorVariants.find(function(variant) {
    return variant.available;
  });

  if (firstAvailable) {
    return firstAvailable;
  }

  return sameColorVariants[0] || null;
}

// =============================================================================
// class ProductGallery
//
// Manages the product image/video/model gallery. Handles:
//   - Glider.js carousel initialisation for main (featured) and thumbnail tracks.
//   - Thumbnail click ? variant sync (via vChange / vChangeByMediaId).
//   - Glider drag ? variant sync via vChangeByMediaId() on slide-visible event.
//   - Video (Shopify Plyr) and 3D model (Shopify ModelViewerUI) lazy loading.
//   - Image zoom via ProductZoom instances.
//   - Shopify XR (AR quick look) for 3D models.
//
// Two internal variant?media sync functions (closures inside constructor):
//
//   vChange(image) - LEGACY path:
//     Uses the variantImages lookup built from variant.featured_image.src.
//     Matches thumbnail image URL to option values, then sets radio inputs
//     and dispatches a 'change' event. This path is used when the thumbnail
//     does not carry data-linked-* or data-media attributes.
//
//   vChangeByMediaId(mediaId, sourceEl) - MODERN path:
//     Uses the Normalized JSON data (normalizedData.media_variant_map) and
//     data-linked-* payload to resolve and apply the best matching variant.
//     Sets all option inputs, updates hidden [name='id'], stores PVR state,
//     dispatches 'change' on [data-product-options].
//     This is the preferred path and is tried first on every thumbnail click.
//
// selectMediaByVariantId(variantId) - public method:
//   Called by ProductDetails after variant change to sync the gallery to the
//   new variant's featured media. Finds the figure with matching data-media
//   attribute and calls _selectMediaByIndex().
//
// syncMediaToCurrentVariant() - public method:
//   Called once on initialisation to sync the gallery to the page-load variant.
//
// Instantiated by: ProductDetails constructor.
// =============================================================================
var ProductGallery = function () {
  function ProductGallery(el) {      
    var _this = this,
        settings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    ProductGallery_classCallCheck(this, ProductGallery);
    this.el = el;      
    this.settings = settings;
    var sectionId = this.settings.section.id;
    this.viewport = this.el.querySelector('#main-product-image-' + sectionId);  
    this.featured = this.el.querySelector('#featured-' + sectionId);
    this.navigation = this.el.querySelector('#thumbnails-' + sectionId);
    this.thumbnails = this.el.querySelectorAll('.thumbnails-' + sectionId + ' .button');
    this.figures = this.viewport.querySelectorAll('figure');
    this.selected = {
      figure: this.viewport.querySelector('figure.visible'),
      thumbnail: null
    };
    this.media = {
      images: [],
      models: [],
      videos: []
    };
    var variantImages = {},
        variantMediaMap = {},
        thumbnails,
        variantImage,
        opt_key,
        opt_val,
        loop_index,
        // PHASE 3: product_info.variants -> data.variants; product_info.options -> data.options_with_values.
        vars = settings.section.data.variants,
        opts = settings.section.data.options_with_values,
        optionsTypes = {
          select: 'select',
          radio: 'radio'
        };
    function vChange(image) {
      const liveSelector = getLiveProductOptionsContainerFromGallery(_this.el);
      const liveOptions = getLiveSelectorOptionsFromGallery(_this.el);

      if (!liveSelector || !liveOptions.length || !variantImages[image]) {
        return false;
      }

      let matchedAny = false;

      opts.forEach(function(o, i) {
        loop_index = 'option-' + i;

        if (variantImages[image][loop_index] !== null) {
          liveOptions.forEach(function(o) {
            if (String(o.value) === String(variantImages[image][loop_index])) {
              if (o.type === 'radio' || o.type === 'checkbox') {
                o.checked = true;
                o.setAttribute('checked', 'checked');

                const swatchElement = o.closest('.swatch-element');
                if (swatchElement) {
                  const wrapper = o.closest('[data-option-index]');
                  if (wrapper) {
                    wrapper.querySelectorAll('.swatch-element').forEach(function(el) {
                      el.classList.remove('active');
                    });
                  }
                  swatchElement.classList.add('active');
                }
              }

              theme.thumbID = true;
              theme.thumbVAR = o;

              const variantInput = _this.el.closest('section').querySelector('[name="id"]');
              if (variantInput && o.dataset.variantId) {
                variantInput.value = o.dataset.variantId;
              }

              matchedAny = true;
            }
          });
        }
      });

      if (matchedAny) {
        liveSelector.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return matchedAny;
    }
    
    function vChangeByMediaId(mediaId, sourceEl) {
      const form = getLiveProductFormFromGallery(_this.el);
      if (!form) return false;

      const normalizedData =
        getNormalizedDataFromGallery(_this.el) ||
        (_this.settings.section.data ? _this.settings.section.data.normalized : null);

      let matchedVariant = null;

      const linkedPayload = getLinkedVariantPayloadFromElement(sourceEl);
      if (linkedPayload) {
        matchedVariant = resolveVariantFromLinkedPayload(form, linkedPayload, normalizedData);
      }

      if (!matchedVariant && mediaId) {
        const mappedVariants = getMediaMappedVariants(normalizedData, mediaId);
        if (mappedVariants.length) {
          const currentSelectedOptions = getSelectedOptionNamesFromForm(form);
          matchedVariant = getRetainedOrFirstAvailableVariantFromMedia(mappedVariants, currentSelectedOptions, form);
        }
      }

      if (!matchedVariant) return false;

      const optionWrappers = form.querySelectorAll('[data-product-options] [data-option-index]');
      const expectedValues = [matchedVariant.option1, matchedVariant.option2, matchedVariant.option3];

      let triggerEl = null;

      optionWrappers.forEach(function(wrapper) {
        const idx = Number(wrapper.getAttribute('data-option-index'));
        const expectedValue = expectedValues[idx];
        if (!expectedValue) return;

        const select = wrapper.querySelector('select.single-option-selector');
        if (select) {
          const matchingOption = Array.from(select.options).find(function(opt) {
            return String(opt.value) === String(expectedValue);
          });

          if (matchingOption) {
            select.value = matchingOption.value;
            if (!triggerEl) {
              triggerEl = select;
            }
          }
          return;
        }

        const inputs = Array.from(wrapper.querySelectorAll('.selector-option'));
        let matchingInput = null;

        inputs.forEach(function(input) {
          input.checked = false;
          input.removeAttribute('checked');

          const swatchElement = input.closest('.swatch-element');
          if (swatchElement) {
            swatchElement.classList.remove('active');
          }

          if (String(input.value) === String(expectedValue)) {
            matchingInput = input;
          }
        });

        if (matchingInput) {
          matchingInput.checked = true;
          matchingInput.setAttribute('checked', 'checked');

          const swatchElement = matchingInput.closest('.swatch-element');
          if (swatchElement) {
            swatchElement.classList.add('active');
          }

          if (!triggerEl) {
            triggerEl = matchingInput;
          }
        }
      });

      const variantInput = form.querySelector('[name="id"]');
      if (variantInput) {
        variantInput.value = matchedVariant.id;
      }

      const pvrContext = getPvrProductContext(form);
      if (pvrContext) {
        const resolvedPvrVariant = getPvrVariantObjectById(form, matchedVariant.id) || matchedVariant;
        theme.pvrResolvedVariantForReload = resolvedPvrVariant;
        // pvrOptionIdsForReload removed: no longer read anywhere.
      } else {
        // Non-PVR: set null so reLoad() skips applyResolvedPvrVariantToForm.
        // Form state already correctly set by the optionWrappers loop above.
        // Setting matchedVariant here caused reLoad() to overwrite Size with
        // the thumbnail matched variant's Size, losing the user's Size selection.
        theme.pvrResolvedVariantForReload = null;
      }

      theme.thumbID = true;
      theme.thumbVAR = triggerEl || null;

      const liveSelector = getLiveProductOptionsContainerFromGallery(_this.el);
      if (!liveSelector) return false;

      // Capture the resolved option values before dispatching the change event.
      // The theme's Swatches() function listens to the same change event and
      // resets Size to the first available option for the new Color -- it runs
      // synchronously before our handlers. Capturing and restoring via
      // setTimeout(0) runs after all synchronous event handling completes,
      // safely restoring the correct Size without triggering further events.
      var capturedOption1 = matchedVariant.option1 || null;
      var capturedOption2 = matchedVariant.option2 || null;
      var capturedOption3 = matchedVariant.option3 || null;
      var capturedForm    = form;
      var capturedVarId   = matchedVariant.id;

      liveSelector.dispatchEvent(new Event('change', { bubbles: true }));

      // Restore the correct option states after Swatches() has run.
      setTimeout(function() {
        forceOptionValueInForm(capturedForm, 0, capturedOption1);
        forceOptionValueInForm(capturedForm, 1, capturedOption2);
        forceOptionValueInForm(capturedForm, 2, capturedOption3);
        var vi = capturedForm.querySelector('[name="id"]');
        if (vi) vi.value = capturedVarId;
        updateOptionLabels(capturedForm);
        restoreSwatchLabels(capturedForm);
      }, 0);

      return true;
    }
    
    if (this.featured) {
      this.slideshow = true;
      this.slideshow_section = false;      
      var FCarousel = document.getElementById('featured-'+ sectionId);      
      if (FCarousel) {
        this.active = this.featured.querySelector('.active .media-container');
        this.fthumbnails = this.viewport.querySelectorAll('.thumbs');
        this.imageHeight(this.active);
        var mSI = FCarousel.querySelector('.active').getAttribute('data-product-gallery-figure'),
            next = document.getElementById('glider-featured-button-next-thumbs-' + sectionId),
            prev = document.getElementById('glider-featured-button-prev-thumbs-' + sectionId),
            glider = new Glider(FCarousel, {
              arrows: {
                prev: prev,
                next: next
              },
              draggable: true,
              rewind: true,
              scrollLock: true,
              skipTrack: true,
              slidesToShow: 1,
              slidesToScroll: 1
            });
        if (glider) {
          FCarousel.querySelector('.glider-track.slideshow').classList.add('loaded');
          // This is an initention dup to avoid the initial slide when loading a variant that is not the first
          Glider(this.featured).scrollItem(parseInt(mSI));
          Glider(this.featured).scrollItem(parseInt(mSI));
          FCarousel.addEventListener('glider-slide-visible', function(ev) {
            if (theme.busy) {
              _this.fthumbnails.forEach(function (t) {
                t.disabled = true;
              });
              return;
            } else {
              setTimeout(function() {
                theme.busy = false;
                _this.fthumbnails.forEach(function (t) {
                  t.disabled = false;
                });
              }, 350);
            }
            theme.busy = true;            
            if (theme.thumb) {
              theme.thumb = false;
              return;
            }            
            const activeFigure = FCarousel.querySelector('.active');
            const activeMediaId = activeFigure ? activeFigure.getAttribute('data-media') : null;

            if (theme.featuredDragging && activeMediaId) {
              theme.featuredDragging = false;

              if (vChangeByMediaId(activeMediaId, activeFigure)) {
                return;
              }
            }

            _this._selectMediaByIndex(FCarousel.querySelector('.active').getAttribute('data-product-gallery-figure'));          
          });
        }
      };
      var resizeTimer;
      window.addEventListener('resize', function(b) {
        if (cW == window.innerWidth) {
          return;
        }
        cW = window.innerWidth;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function() {
          _this.imageHeight(_this.active);
        }, 250);
      });
    }
    if (this.navigation) {
      this.selected.thumbnail = this.navigation.querySelector('[data-product-gallery-selected="true"]');
      this.slideshow = true;
      this.slideshow_section = false;
      var TCarousel = document.getElementById('thumbnails-'+ sectionId);  
      if (TCarousel) {
        var mSI = this.selected.thumbnail.getAttribute('data-product-gallery-thumbnail'),
            next = document.getElementById('glider-button-next-thumbs-' + sectionId),
            prev = document.getElementById('glider-button-prev-thumbs-' + sectionId),
            glider = new Glider(TCarousel, {
              arrows: {
                prev: prev,
                next: next
              },
              draggable: true,
              dragVelocity:2.5,
              resizeLock: false,
              skipTrack: true, 
              slidesToShow: 4,
              slidesToScroll: 4
            });
        if (glider) {
          Glider(this.navigation).scrollItem(parseInt(mSI));
          TCarousel.classList.add('loaded');
        }
      };
    }
    vars.forEach(function(v) {
      variant = v;

      const mediaId = getVariantMediaId(variant);
      if (mediaId) {
        variantMediaMap[String(mediaId)] = variantMediaMap[String(mediaId)] || [];
        variantMediaMap[String(mediaId)].push(variant);
      }

      if (typeof variant.featured_image !== 'undefined' && variant.featured_image !== null) {
        variantImage = variant.featured_image.src.split('?')[0].replace(/http(s)?:/,'');
        variantImages[variantImage] = variantImages[variantImage] || {};
        var opts = v.options;
        opts.forEach(function(o,i) {
          opt_key = 'option-'+i;
          opt_val = o;
          if (typeof variantImages[variantImage][opt_key] === 'undefined') {
            variantImages[variantImage][opt_key] = opt_val;
          }
          else {
            var oldValue = variantImages[variantImage][opt_key];
            if (oldValue !== null && oldValue !== opt_val) {
              variantImages[variantImage][opt_key] = null;
            }
          }
        });
      }
    });
    this.thumbnails.forEach(function(thumbnail) {
      thumbnail.onclick = function(e) {
        theme.thumb = true;

        const mediaId = this.getAttribute('data-media');
        if (vChangeByMediaId(mediaId, this)) {
          _this._selectMediaByIndex(this.getAttribute('data-product-gallery-thumbnail'));
          e.preventDefault();
          return false;
        }

        var thumb = this.querySelector('img[src]');
        if (thumb) {
          var image = thumb.getAttribute('src').split('?')[0].replace(/(_1x)/,'');
          if (typeof variantImages[image] !== 'undefined') {
            if (vChange(image)) {
              e.preventDefault();
              return false;
            }
          }
        }

        _this._selectMediaByIndex(this.getAttribute('data-product-gallery-thumbnail'));
        e.preventDefault();
        return false;
      };
    });
    var features = [];
    if (this.settings.zoom_enable && this.selected.figure.dataset.mediaType === 'image') {
      this._initZoom(this.selected.figure);
    }
    if (this.el.querySelector('[data-media-type="model"]')) {
      this.viewport.onclick = function(e){
        if ('shopifyXr' in e.target.dataset) {
          _this._onViewInYourSpaceClick(e.target);
        }
      };
      features.push({
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this._onModelLibraryLoad.bind(this)
      });
      features.push({
        name: 'shopify-xr',
        version: '1.0'
      });      
    }
    if (this.el.querySelector('[data-media-type="video"]')) {
      Promise.all([new Promise(function (resolve) {
        if (!document.querySelector('#plyr-stylesheet')) {
          var link = document.createElement('link');
          link.setAttribute('id', 'plyr-stylesheet');
          link.setAttribute('rel', 'stylesheet');
          link.setAttribute('href', 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css');
          link.onload = resolve;
          document.body.appendChild(link);
        } else {
          resolve();
        }
      }), new Promise(function (resolve) {
        features.push({
          name: 'video-ui',
          version: '1.0',
          onLoad: resolve
        });
      })]).then(this._onVideoLibraryLoad.bind(this));
    }
    if (features.length) {
      Shopify.loadFeatures(features);
    }
  }
  ProductGallery_createClass(ProductGallery, [{
    key: "selectMediaByVariantId",
    value: function selectMediaByVariantId(variantId) {
      if (!variantId) return;

      // PHASE 3: getNormalizedDataFromGallery() now reads ProductData.
      // section.data.normalized aliased to section.data; fallbackVariants unreachable.
      const normalizedData =
        getNormalizedDataFromGallery(this.el) ||
        (this.settings.section.data ? this.settings.section.data.normalized : null);

      const normalizedVariants = (
        normalizedData &&
        normalizedData.variants
      ) ? normalizedData.variants : [];

      // PHASE 3: fallbackVariants simplified -- product_info removed from schema.
      const fallbackVariants = [];

      const variant = normalizedVariants.find(function(v) {
        return String(v.id) === String(variantId);
      }) || fallbackVariants.find(function(v) {
        return String(v.id) === String(variantId);
      });

      if (!variant) return;

      let mediaId = null;

      if (variant.featured_media && variant.featured_media.id) {
        mediaId = variant.featured_media.id;
      } else if (variant.featured_image && variant.featured_image.id) {
        mediaId = variant.featured_image.id;
      } else if (variant.featured_media_id) {
        mediaId = variant.featured_media_id;
      }

      if (!mediaId) return;

      const figure = this.viewport.querySelector('[data-media="' + mediaId + '"]');
      if (!figure) return;

      this._selectMediaByIndex(figure.dataset.productGalleryFigure);
    }
  }, {
    key: "syncMediaToCurrentVariant",
    value: function syncMediaToCurrentVariant() {
      const form = this.el.closest('section').querySelector('[data-product-form]');
      if (!form) return;

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput || !variantInput.value) return;

      this.selectMediaByVariantId(variantInput.value);
    }
  }, {
    key: "imageHeight",
    value: function imageHeight(a) {
      var newHeight = a.offsetHeight;
      this.featured.querySelector('.glider-track').style.height = newHeight + 'px';      
    }
  }, {
    key: "selectMediaByVariant",
    value: function selectMediaByVariant(t) {        
      if (t.dataset.variantImg) {
        var figure = this.viewport.querySelector("[data-media=\"".concat(t.dataset.variantImg, "\"]"));
      } else {
        var figure = this.viewport.querySelector("[data-product-gallery-figure='0']");
      }
      if (!figure) return;
      this._selectMediaByIndex(figure.dataset.productGalleryFigure);          
    }
  }, {
    key: "pauseVideos",
    value: function pauseVideos() {
      this.el.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      this.el.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      var excludeVideo = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      this.media.videos.forEach(function (v) {
        if (v !== excludeVideo) {
          v.figure.querySelector('.plyr').tabIndex = -1;
          v.pause();
        }
      });
    }
  }, {
    key: "pauseModels",
    value: function pauseModels() {
      var excludeModel = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      this.media.models.forEach(function (m) {
        if (m !== excludeModel && m.ui.interacting) {
          m.ui.pause();
        }
      });
    }
  }, {
    key: "unload",
    value: function unload() {
      this.events.unregisterAll();
      this.media.videos.forEach(function (v) {
        return v.unload();
      });
      this.media.images.forEach(function (i) {
        return i.unload();
      });
    }
  }, {
    key: "_onModelLibraryLoad",
    value: function _onModelLibraryLoad() {
      var _this2 = this,
          controls = ['zoom-in', 'zoom-out'];      
      theme.LibraryLoader.load('modelViewerUiStyles');
      if (document.fullscreenEnabled) controls.push('fullscreen');
      this.viewport.querySelectorAll('[data-media-type="model"]').forEach(function (modelFigure) {
        var modelEl = modelFigure.querySelector('model-viewer');
        _this2.media.models.push({
          ui: new Shopify.ModelViewerUI(modelEl, {
            controls: controls
          }),
          el: modelEl,
          figure: modelFigure
        });
        modelEl.addEventListener('shopify_model_viewer_ui_toggle_play', function(evt) {
          _this2.viewport.querySelector('.glider').classList.add('no-drag');
          theme.model = true;
        }.bind(this));
        modelEl.addEventListener('shopify_model_viewer_ui_toggle_pause', function(evt) {
          _this2.viewport.querySelector('.glider').classList.remove('no-drag');
          theme.model = false;
        }.bind(this));
      });
    }
  }, {
    key: "_onViewInYourSpaceClick",
    value: function _onViewInYourSpaceClick(target) {
      if (target.dataset.shopifyModel3dId === this.selected.figure.dataset.media) return;
      var figure = this.viewport.querySelector("[data-media=\"".concat(target.dataset.shopifyModel3dId, "\"]"));
      this._selectMediaByEl(figure);
    }
  }, {
    key: "_selectMediaByEl",
    value: function _selectMediaByEl(el) {
      this._selectMediaByIndex(parseInt(el.dataset.productGalleryFigure, 10));
    }
  }, {
    key: "_onVideoLibraryLoad",
    value: function _onVideoLibraryLoad() {      
      var _this3 = this,
          videoFigures = this.viewport.querySelectorAll('[data-media-type="video"]'),
          _loop = function _loop(i) {
        var videoFigure = videoFigures[i],
            videoEl = videoFigure.querySelector('video'),
            player = new Shopify.Plyr(videoEl, {
          loop: {
            active: _this3.settings.loop_enable
          }
        });
        var video = {
          figure: videoFigure,
          el: videoEl,
          player: player,
          restart: function restart() {
            player.restart();
            player.play();
          },
          pause: function pause() {
            return player.pause();
          },
          play: function play() {
            return player.play();
          },
          unload: function unload() {
            return player.destroy();
          }
        };
        videoFigure.play = function(e){
          return _this3.pauseVideos(video);
        }        
        _this3.media.videos.push(video);
      };
      for (var i = 0; i < videoFigures.length; i++) {
        _loop(i);
      }
      videoFigures.forEach(function(v) {
        v.querySelector('.plyr').tabIndex = -1;
      });
    }
  }, {
    key: "_onVideoSelect",
    value: function _onVideoSelect(video) {
      video.figure.querySelector('.plyr').tabIndex = 0;
      if (!theme.detectmob) {
        video.play();
      }
    }
  }, {
    key: "_onMediaSelect",
    value: function _onMediaSelect(index) {
      var _this8 = this,
          focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false,
          figure = this.figures[index];
      this.selected.figure.dataset.productGallerySelected = false;
      this.selected.figure.tabIndex = -1;
      this.selected.figure = figure;
      this.selected.figure.dataset.productGallerySelected = true;
      this.selected.figure.tabIndex = 0;
      var viewInYourSpaceEls = this.el.querySelectorAll('[data-shopify-xr]'),
          setViewInYourSpaceID = function setViewInYourSpaceID(id) {
        for (var i = 0; i < viewInYourSpaceEls.length; i++) {
          viewInYourSpaceEls[i].dataset.shopifyModel3dId = id;            
        }
      };
      if (viewInYourSpaceEls.length) {
        setViewInYourSpaceID(viewInYourSpaceEls[0].dataset.defaultModelId);
      }
      this.pauseVideos();
      this.pauseModels();
      this.media.models.forEach(function (m) {
        if (m.figure === _this8.selected.figure) {
          if (!theme.detectmob) {
            m.ui.play();
          }
        }
      });
      switch (this.selected.figure.dataset.mediaType) {
        case 'external_video':
          let f = this.selected.figure;
          if (!f.getAttribute('loaded') && f.querySelector('template')) {
            f.querySelector('.youtube-container').appendChild(f.querySelector('template').content.firstElementChild.cloneNode(true));
            f.setAttribute('loaded', true);
          }
          f.focus();
          break;
        case 'video':          
          let v = this.selected.figure;
          if (v.classList.contains('visible-video')) {
            v.classList.remove('visible-video')
          } else {
            this._onVideoSelect(this.media.videos.filter(function (v) {
              return v.figure === _this8.selected.figure;
            })[0]);
          }          
          break;
        case 'model':
          setViewInYourSpaceID(this.selected.figure.dataset.media);
          break;
        case 'image':
          if (this.settings.zoom_enable && this.selected.figure.dataset.mediaType === 'image') {
            this._initZoom(this.selected.figure);
          };
          break;
        default:
          break;
      }
    }
  }, {
    key: "_selectMediaByIndex",
    value: function _selectMediaByIndex(index) {
      var _this = this,
          focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false,
          mSI = index;
      if (this.featured) {
        Glider(this.featured).scrollItem(parseInt(mSI));        
        setTimeout(function() {
          _this.imageHeight(_this.featured.querySelector('[data-product-gallery-figure="' + mSI + '"] .media-container'));
        }, 250);
      }
      if (this.navigation) {
        Glider(this.navigation).scrollItem(parseInt(mSI));
        var thumbnail = this.thumbnails[index];
        if (this.selected.thumbnail) {
          this.selected.thumbnail.dataset.productGallerySelected = false;
        }
        this.selected.thumbnail = thumbnail;
        this.selected.thumbnail.dataset.productGallerySelected = true;
      }
      _this._onMediaSelect(index, focus);
    }
  }, {
    key: "_initZoom",
    value: function _initZoom(figure) {
      var exists = this.media.images.filter(function (image) {
        return image.el.dataset.media === figure.dataset.media;
      });
      if (this.media.images.length === 0 || exists.length === 0) {
        this.media.images.push(new ProductZoom(figure));
      }
    }
  }]);
  return ProductGallery;
}();

// =============================================================================
// StoreAvailabilityLoad(theme_pJ, container)
//
// Initialises the in-store pickup availability widget.
// Finds the [data-store-availability-container] within container and instantiates
// a StoreAvailability object, then calls updateContent() with the current variant ID.
//
// StoreAvailability.updateContent(variantId):
//   LEGACY AJAX: fetches the 'store-availability' section HTML for the given
//   variant ID via Shopify's section render API:
//     baseUrl + 'variants/' + variantId + '/?section_id=store-availability'
//   Replaces container.innerHTML with the response. Then opens it via MicroModal
//   when the .srfc button is clicked.
//
// Called from: ProductDetails constructor and reLoad() after variant change.
// =============================================================================
function StoreAvailabilityLoad(theme_pJ, container) {  
  var sectionId = container.getAttribute('data-section-id'),
      sectionType = container.getAttribute('data-section-type');
  if (!theme_pJ) {
    return;
  }
  this.container = container;
  var sectionId = container.getAttribute('data-section-id');
  this.selectors = {
    storeAvailabilityContainer: '[data-store-availability-container]',
    originalSelectorId: '#product-select-' + sectionId,
    singleOptionSelector: 'form.product-form-' + sectionId + ' .single-option-selector'
  };
  this.storeAvailabilityContainer = container.querySelector(
    this.selectors.storeAvailabilityContainer
  );
  if (this.storeAvailabilityContainer) {
    this.storeAvailability = new StoreAvailability(this.storeAvailabilityContainer);
  }
  if (this.storeAvailability) {
    this.storeAvailability.updateContent(
      container.querySelector('[name="id"]').value,
      // PHASE 3: title is top-level in unified schema (was product_info.title).
      theme_pJ.title
    );
  }  
};

StoreAvailability = (function(container) {
  var selectors = {
    storeAvailabilityModalProductTitle: '[data-store-availability-modal-product-title]'
  };
  function StoreAvailability(container) {
    this.container = container;
    this.sectionId = this.container.getAttribute('data-section-id');
    this.pT = this.container.dataset.productTitle;
  }
  StoreAvailability.prototype = Object.assign({}, StoreAvailability.prototype, {
    updateContent: function(variantId) {
      let baseUrl = this.container.dataset.baseUrl;
      if (!baseUrl.endsWith('/')) {
        baseUrl = baseUrl + '/';
      }
      var variantSectionUrl =
          baseUrl +
          'variants/' +
          variantId +
          '/?section_id=store-availability';
      var self = this;
      fetch(variantSectionUrl)
      .then(function(response) {
        return response.text();
      })
      .then(function(storeAvailabilityHTML) {
        if (storeAvailabilityHTML.trim() === '') {
          return;
        }
        self.container.innerHTML = storeAvailabilityHTML;
        self.container.innerHTML = self.container.firstElementChild.innerHTML;
        var storeAvailabilityModalProductTitle = self.container.querySelector(
          selectors.storeAvailabilityModalProductTitle
        );
        if (storeAvailabilityModalProductTitle) {
          storeAvailabilityModalProductTitle.textContent = self.pT;
        }
        var el = self.container.querySelector('.srfc');        
        const body = document.body;
        const content = self.container.querySelector('.StoreAvailabilityModal');
        if (!el) return;
        const m = document.querySelector('#avail-' + variantId);
        const mc = content.querySelector('.modal-container');
        const aB = document.getElementById('ajaxBusy');
        el.onclick = (e) => {          
          try {
            MicroModal.show('avail-' + variantId, {
              onShow: function(modal) {
                m.classList.add('loaded');
                mc.classList.add('loaded');
                aB.style.display = 'block';
                document.body.classList.add('modal-active');
              },
              onClose: function(modal) {
                setTimeout(function() {
                  m.classList.remove('loaded');
                  document.body.classList.remove('modal-active');
                }, 250);
                mc.classList.remove('loaded');
                aB.style.display = 'none';
              }
            });
          } catch (e) {
            console.log("micromodal error: ", e);
          }
          e.preventDefault();
        }
      });
    },
  });
  return StoreAvailability;
})();

// =============================================================================
// Tooltip(container)
// Initialises size-chart and info tooltip modals within container.
// On .tooltip element click: reads data-container attribute to find the JSON
// content element, then opens #quick-modal via MicroModal with that content.
// Called from: ProductDetails constructor and reLoad().
// =============================================================================
function Tooltip(container) {  
  var el = container.querySelectorAll('.tooltip'),
      m = document.querySelector('#quick-modal'),
      mc = m.querySelector('.modal-container'),
      mcC = mc.querySelector('.modal-content'), 
      aB = document.getElementById('ajaxBusy');
  el.forEach(function(e) {
    e.onclick = function(ev){      
      const content = this.getAttribute('data-container'),
            content_container = this.parentNode;
      e.focus();
      aB.style.display = 'block';
      const section = JSON.parse(content_container.querySelector('.' + content).innerText);
      MicroModal.show('quick-modal', {
        onShow: function(modal) {
          m.classList.add('loaded');
          mc.classList.add('loaded');
          document.body.classList.add('modal-active');
        },
        onClose: function(modal) {              
          setTimeout(function() {
            m.classList.remove('loaded');
            document.body.classList.remove('modal-active');            
            mcC.innerHTML = '';            
          }, 250);
          mc.classList.remove('loaded');
          aB.style.display = 'none';
        }
      });    
      m.classList.add('loaded');
      mcC.innerHTML = section;
    };
  });
};

// VarHover() -- REMOVED.
// Was the legacy AJAX hover/focus/touch prefetch trigger.
// Attached self-removing no-op listeners after Phase 5 retired the fetch logic.
// Removed in final cleanup: function was never called after Phase 6D/E removed
// the call sites in _onVariantChange(firstLoad) and reLoad().

// VariantLoad() -- REMOVED.
// Was the legacy AJAX bulk prefetch that fired up to 1,020 fetch() requests on
// page load (all Style x Color x Size combinations). Replaced by the unified
// ProductData JSON node (Phase 2). Function shell had set variants_loaded = true
// but was never called after Phase 6D removed the call site in firstLoad.
// The variants_loaded flag and its read guard are also removed (nothing adds
// the 'unavailable' class to the form in the current code path).

function ProductDetails_classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function ProductDetails_defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }
function ProductDetails_createClass(Constructor, protoProps, staticProps) { if (protoProps) ProductDetails_defineProperties(Constructor.prototype, protoProps); if (staticProps) ProductDetails_defineProperties(Constructor, staticProps); return Constructor; }
// =============================================================================
// class ProductDetails
//
// The central product page controller. Coordinates all sub-systems:
//   - ProductGallery (image/media gallery)
//   - ProductForm + VariantHelper (variant selection events)
//   - Quantity controls
//   - updateVariantUIFromJSON() on load and variant change
//   - StoreAvailabilityLoad for in-store pickup
//   - Tooltip for size charts / info modals
//   - Gift card UX
//
// Constructor initialisation order:
//   1. Store refs: this.el, this.section, this.data, this.$vars etc.
//   2. If .product-images exists: new ProductGallery() + syncMediaToCurrentVariant().
//   3. listenForVariantChange(): attaches 'product-variant-switch' listener.
//   4. If not onboarding: new ProductForm() ? new VariantHelper() ? _switchVariant(true).
//   5. Quantity() -- once only; attaches -/+ listeners and button state re-evaluation.
//   6. updateVariantUIFromJSON() with initial variant ID (Strategy B, page load).
//   7. StoreAvailabilityLoad() if store availability container exists.
//   8. Tooltip().
//   9. Gift card checkbox handler.
//
// _onVariantChange() flow (on 'product-variant-switch' event from VariantHelper):
//   firstLoad == true:
//     Return early -- UI already set by constructor. No AJAX, no vLoad.
//   Normal change:
//     A. Identify lastFocusedElementID (variant ID from radio/select data-variant-id,
//        or PVR resolved variant stored in theme.pvrResolvedVariantForReload).
//     B. If data-product-url on focused element: navigate to that URL (PVR redirect).
//     C. If gallery exists and no PVR reload pending: sync gallery media.
//     D. updateVariantUIFromJSON() -- Strategy B immediate JSON-driven UI update.
//     E. Resolve targetId: lastFocusedElementID or getNormalizedVariantBySelectedOptions.
//     F. reLoad(): applies PVR variant to form, syncs gallery, calls
//        updateVariantUIFromJSON, Cart, StoreAvailabilityLoad, Tooltip,
//        and applySwatchAvailability.
//
// Instantiated by: ProductLoad constructor.
// =============================================================================
var ProductDetails = function () {
  function ProductDetails(_ref, force) {
    var _this = this,
        $el = _ref.$el,
        section = _ref.section;
    ProductDetails_classCallCheck(this, ProductDetails);
    this.el = $el;
    this.section = section;
    this.data = section.data;
    this.onboarding = this.data.onboarding;
    this.postMessage = section.postMessage;
    this.type = section.type;
    this.$el = $el;
    this.$window = window;
    this.varID = document.getElementById('variantId-' + section.id).value;
    this.$productInfo = $el.querySelector('#product-details-' + section.id);
    this.$atc = $el.querySelector('#add-to-cart-' + section.id);
    this.$vars = $el.querySelector('[data-product-options]');
    if (!this.$vars) {
      $el.querySelector('[data-product-form]').classList.remove('disabled');
    }
    this._handleVariantChange = this._handleVariantChange.bind(this);
    if (this.el.querySelector('.product-images')) {
      this.productGallery = new ProductGallery(this.el.querySelector('.product-images'), {
        loop_enable: this.data.loop_enable,
        zoom_enable: this.data.zoom_enable,
        section: section
      });
      this.productGallery.syncMediaToCurrentVariant();
    }
    this.listeningForVariantChange = false;
    this.listenForVariantChange();
    if (!this.onboarding) {
      this.form = new ProductForm(this.$productInfo, {        
        form: this.$el.querySelector('[data-product-form]'),
        productEl: this.$el,
        theme_pJ: _ref.theme_pJ,
        // PHASE 3: product_info -> unified data root; product_variants -> options_with_values.
        product: this.data,
        productVariants: this.data.options_with_values,
        type: this.type,
        moneyFormat: theme.moneyFormat,
        postMessage: section.postMessage,
        useHistory: this.data.use_history
      });
    }
    Quantity(this.$el);
    {
      const initialVariantInput = this.$el.querySelector('[name="id"]');
      if (initialVariantInput && initialVariantInput.value) {
        updateVariantUIFromJSON(this.$el, this.section.data, initialVariantInput.value, this.section.id);
      }
    }
    // PHASE 4: Apply initial swatch availability on page load.
    // updateVariantUIFromJSON() above calls applySwatchAvailability() internally,
    // but only when a variant is resolved. This explicit call ensures availability
    // is set even if the initial variant input is empty (edge case on page load).
    {
      const initForm = this.$el.querySelector('[data-product-form]');
      const initSelected = initForm ? getSelectedOptionNamesFromForm(initForm) : [];
      const initAvailMap = computeSwatchAvailability(this.section.data, initSelected);
      applySwatchAvailability(this.$el, initAvailMap);
      // Dim gallery thumbnails on initial page load.
      var initIsPvr = !!(this.section.data && this.section.data.is_shsd_parent);
      applyGalleryAvailability(this.$el, this.section.data, initIsPvr);
    }
    if (this.$el.querySelector('.product-single__store-availability-container')) {
      StoreAvailabilityLoad(_ref.theme_pJ, this.$el);
    }    
    Tooltip(this.$el);
    var pg = $el.querySelector('.product-gift-' + section.id);
    if (pg) {
      var i = pg.querySelector('input[type="checkbox"]'),
          d = pg.querySelector('details');
      i.onchange = function(ev){
        d.toggleAttribute('open')
      }
    }
  }
  ProductDetails_createClass(ProductDetails, [{
    key: "listenForVariantChange",
    value: function listenForVariantChange() {
      if (!this.listeningForVariantChange) {
        this.$el.querySelector('[data-product-form]').addEventListener('product-variant-switch', this._handleVariantChange);
        this.listeningForVariantChange = true;
      }
    }
  }, {
    key: "stopListeningForVariantChange",
    value: function stopListeningForVariantChange() {
      if (this.listeningForVariantChange) {
        this.$el.querySelector('[data-product-form]').removeEventListener('product-variant-switch', this._handleVariantChange);
        this.listeningForVariantChange = false;
      }
    }
  },{
    key: "unload",
    value: function unload() {
      this.stopListeningForVariantChange();
    }
  }, {
    key: "_handleVariantChange",
    value: function _handleVariantChange(event) {
      var data = event.detail;
      this._onVariantChange(data);
    }
  }, {
    key: "_onVariantChange",
    value: function _onVariantChange(_ref2, force) {
      var _this = this,
          isProduct = _ref2.isProduct,
          form = this.$el.querySelector('[data-product-form]');
      // PHASE 6: vLoad init, VariantLoad, and VarHover calls removed.
      // VariantLoad sets variants_loaded=true immediately (Phase 5A).
      // VarHover is a no-op shell (Phase 5B). vLoad never populated.
      if (_ref2.firstLoad == true) {
        return;
      } else if (isProduct && !isProduct(this.data)) {
        return;
      }
      if (theme.thumbID) {
        var lastFocusedElement = theme.thumbVAR || null,
            lastFocusedElementID = (theme.pvrResolvedVariantForReload && theme.pvrResolvedVariantForReload.id)
              ? theme.pvrResolvedVariantForReload.id
              : (theme.thumbVAR && theme.thumbVAR.dataset ? theme.thumbVAR.dataset.variantId : null);

        if (theme.thumbVAR) {
          if (_ref2.theme_pJ.variant_type == 'radio') {
            var lastFocusedElementIDFocus = theme.thumbVAR.id;
          } else {
            var lastFocusedElementIDFocus = theme.thumbVAR.id || null;
          }
        } else {
          var lastFocusedElementIDFocus = null;
        }

        theme.thumbID = false;
      } else {
        if (_ref2.theme_pJ.variant_type == 'radio') {
          var lastFocusedElement = _ref2.firstLoad.target,
              lastFocusedElementID = _ref2.firstLoad.target.dataset.variantId;
        } else {
          var lastFocusedElement = _ref2.firstLoad.target.options[_ref2.firstLoad.target.selectedIndex],
              lastFocusedElementID = _ref2.firstLoad.target.options[_ref2.firstLoad.target.selectedIndex].dataset.variantId;
        }
        var lastFocusedElementIDFocus = _ref2.firstLoad.target.id;
      }
      if (lastFocusedElement && lastFocusedElement.hasAttribute('data-product-url')) {
        window.location.href = lastFocusedElement.dataset.productUrl;
        return;
      }
      if (this.productGallery && !theme.pvrResolvedVariantForReload) {
        const currentVariantInput = this.$el.querySelector('[name="id"]');
        if (currentVariantInput && currentVariantInput.value) {
          this.productGallery.selectMediaByVariantId(currentVariantInput.value);
        }
      }  
      {
        const currentVariantInput = this.$el.querySelector('[name="id"]');
        if (currentVariantInput && currentVariantInput.value) {
          updateVariantUIFromJSON(this.$el, this.section.data, currentVariantInput.value, this.section.id);
        }
      }
      // variants_loaded guard removed: VariantLoad() (which set the flag) is removed.
      // form.classList.add('unavailable') was in the old fetch branch (Phase 5/6 retired)
      // so the classList.remove() was never doing anything. Both are gone.
      // PHASE 6: vMF block collection removed (replaceReloadableBlocks deleted, Phase 6B).
      // vLoad cache lookup (closestVariant) removed -- VarHover/VariantLoad no longer
      // populate vLoad (Phase 5A/5B). getNormalizedVariantBySelectedOptions() kept as
      // the sole fallback for resolving targetId when lastFocusedElementID is absent.
      var targetId = lastFocusedElementID;

      if (!targetId) {
        const normalizedVariant = getNormalizedVariantBySelectedOptions(this.section.data, form);
        if (normalizedVariant) {
          form.querySelector('[name="id"]').value = normalizedVariant.id;
          targetId = normalizedVariant.id;
        }
      }
      function reLoad(t, f) {
        // PHASE 6: VarHover() call removed -- empty shell (Phase 5B); variants_loaded always true.

        if (theme.pvrResolvedVariantForReload) {
          applyResolvedPvrVariantToForm(f, theme.pvrResolvedVariantForReload);

          forceOptionValueInForm(f, 0, theme.pvrResolvedVariantForReload.option1);
          forceOptionValueInForm(f, 1, theme.pvrResolvedVariantForReload.option2);
          forceOptionValueInForm(f, 2, theme.pvrResolvedVariantForReload.option3);

          const pvrHiddenVariantInput = f.querySelector('[name="id"]');
          if (pvrHiddenVariantInput) {
            pvrHiddenVariantInput.value = theme.pvrResolvedVariantForReload.id;
          }
        }

        if (t.productGallery) {
          const hiddenVariantInput = f.querySelector('[name="id"]');
          if (hiddenVariantInput && hiddenVariantInput.value) {
            t.productGallery.selectMediaByVariantId(hiddenVariantInput.value);
          }
        }

        if (f.querySelector('[data-product-options]') && lastFocusedElementIDFocus) {
          const focusEl = document.getElementById(lastFocusedElementIDFocus);
          if (focusEl) {
            focusEl.focus();
          }
        }

        const currentVariantInput = f.querySelector('[name="id"]');
        if (currentVariantInput && currentVariantInput.value) {
          updateVariantUIFromJSON(t.$el, t.section.data, currentVariantInput.value, t.section.id);
        }

        Cart(t.$el);

        if (t.$el.querySelector('.product-single__store-availability-container')) {
          StoreAvailabilityLoad(_ref2.theme_pJ, t.$el);
        }

        Tooltip(t.$el);
        theme.time = 0;

        // PHASE 4: Re-apply swatch availability after reLoad() has finished
        // rebuilding the DOM. This ensures soldout/hidden states are correct
        // after the AJAX reload block replacement (still active in Phase 4).
        var reloadSelectedOptions = getSelectedOptionNamesFromForm(f);
        var reloadAvailMap = computeSwatchAvailability(t.section.data, reloadSelectedOptions);
        applySwatchAvailability(t.$el, reloadAvailMap);
        // Dim gallery thumbnails after reLoad() rebuilds the DOM.
        var reloadIsPvr = !!(t.section.data && t.section.data.is_shsd_parent);
        applyGalleryAvailability(t.$el, t.section.data, reloadIsPvr);
        updateOptionLabels(f);
        restoreSwatchLabels(f);
      }
      // PHASE 6: replaceReloadableBlocks(vMF,[]) removed -- function deleted (Phase 6B).
      // reLoad() handles all variant state, gallery sync, and UI updates.
      reLoad(this, form);
      resetPvrReloadState();
    }
  }]);
  return ProductDetails;
}();

function ProductLoad_defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }
function ProductLoad_createClass(Constructor, protoProps, staticProps) { if (protoProps) ProductLoad_defineProperties(Constructor.prototype, protoProps); if (staticProps) ProductLoad_defineProperties(Constructor, staticProps); return Constructor; }
// =============================================================================
// class ProductLoad
//
// Section-level bootstrap class. Instantiated once per product section on the page.
//
// Constructor:
//   1. Reads this.section.data from #ProductJson-{sectionId} (legacy schema).
//   2. Reads this.section.data.normalized from #ProductJsonNormalized-{sectionId}
//      via getNormalizedDataForSection().
//   3. Guards against null product_info (returns early for onboarding/placeholder).
//   4. Instantiates ProductDetails.
//   5. Runs RecV() - recently-viewed product tracking via localStorage 'rVP' key.
//      RecV() stores up to 16 recently-viewed product IDs+titles, shifting the oldest
//      out when the limit is reached.
//
// onSectionMessage: forwards Shopify theme editor messages to ProductGallery.
// onSectionUnload: calls ProductDetails.unload() to remove event listeners.
//
// Instantiated by: sectionEvents.forEach() and Section:Loaded event listener (below).
// =============================================================================
var ProductLoad = function () {
  function ProductLoad(section) {
  var pID = document.getElementById('product-select-' + section.id);
  this.$el = section.el;
  this.section = section;
  // PHASE 3: Read from unified ProductData node. normalized aliased to same object.
  this.section.data = JSON.parse(document.getElementById('ProductData-' + section.id).innerHTML);
  this.section.data.normalized = this.section.data;

  if (!this.section.data || !this.section.data.title) {
    return;
  }
  this.productDetails = new ProductDetails({
    $el: this.$el,
    section: section,
    theme_pJ: this.section.data
  });
  function RecV() {
    const pA = [];
    let jR,
        jRA,
        jRAS;
    const nP = 16;
    const pD = {      
      // PHASE 3: product_id and title are top-level fields in unified schema.
      pID: this.section.data.product_id,
      pIT: this.section.data.title
    } 
    pA.push(pD);
    const cpT = pD.pID; 
    const pDS = JSON.stringify(pA);
    const lD = localStorage.getItem('rVP');
    if(lD == null) { 
      localStorage.setItem('rVP', pDS);
    } else if (lD != null ) {
      const opD = localStorage.getItem('rVP');
      const cpD = (opD.match(/pID/g) || []).length;
      const rP =  opD.includes(cpT);
      if (cpD < nP && rP == false) {
        jR = JSON.parse(opD);
        jRA = jR.concat(pA);
        jRAS = JSON.stringify(jRA);
        localStorage.setItem('rVP', jRAS);
      }
      else if (cpD >= nP && rP == false) {
        jR = JSON.parse(opD);
        jR.shift();
        jRA = jR.concat(pA);
        jRA =  JSON.stringify(jRA);
        localStorage.setItem('rVP', jRA);
      }
    }
  }
  RecV();  
  }
  ProductLoad_createClass(ProductLoad, [{
    key: "onSectionMessage",
    value: function onSectionMessage(name, data) {
      if (this.productDetails.productGallery) {
        this.productDetails.productGallery.onSectionMessage(name, data);
      }
    }
  }, {
    key: "onSectionUnload",
    value: function onSectionUnload() {
      if (this.productDetails) this.productDetails.unload();
    }
  }]);
  return ProductLoad;
}();

// =============================================================================
// SECTION INITIALISATION - Two entry points:
//
// 1. 'Section:Loaded' event listener (lines below):
//    For dynamically loaded sections (e.g. after a Shopify theme editor section add
//    or a page loaded via AJAX). Fires when a section container is added to the DOM.
//    Only handles sectionType === 'product-alt' (alternate product section type).
//    Instantiates ProductLoad directly (not via new, so constructor runs as function).
//
// 2. sectionEvents.forEach() loop:
//    Processes section containers that were already in the DOM at script load time
//    (standard page load). Handles both 'product_page' and 'product-alt' types.
//    The 'ignore' class prevents double-initialisation if both loops encounter the
//    same section container.
//
// sectionEvents: expected to be a pre-populated array of CustomEvent objects
//   (set up by the theme's section manager before this script runs).
// =============================================================================
document.addEventListener('Section:Loaded', function(event){
  let sectionContainer = event.detail;
  let sectionType = sectionContainer.dataset.sectionType;
  let sectionId = sectionContainer.dataset.sectionId;
  if(sectionType === 'product-alt'){
    const s = {
      el: sectionContainer, 
      id: sectionId, 
      type: sectionType
    };
    ProductLoad(s);
  }
})

sectionEvents.forEach(function(sectionEvent){
  let sectionContainer = sectionEvent.detail;
  let sectionType = sectionContainer.dataset.sectionType;  
  let sectionId = sectionContainer.dataset.sectionId;
  if(sectionType === 'product_page' || sectionType === 'product-alt' && !sectionContainer.classList.contains('ignore')){
    const s = {
      el: sectionContainer, 
      id: sectionId, 
      type: sectionType
    };
    ProductLoad(s);
    sectionContainer.classList.add('ignore');
  }
})

// =============================================================================
// theme.Product
// Handles the product COLLECTION section (carousel/tabs view of products).
// Separate from the product PAGE - this manages tab navigation and Glider carousel
// initialisation for sections that display multiple products in a tabbed layout.
// Only relevant when data-carousel-type == 'section'.
// onBlockSelect: used in Shopify design mode to activate the selected tab's carousel.
// =============================================================================
theme.Product = (function() {
  function Product(section) {
    var sectionId = section.getAttribute('data-section-id'),
        type = section.getAttribute('data-carousel-type');
    if (type == 'section') {
      Tabs(section);
      if (Shopify.designMode) {        
        var Carousel = document.getElementById('tabs-carousel-' + sectionId);
        Product.prototype.onBlockSelect = function(ev) {
          var tI = section.querySelectorAll('.tabs-id-' + sectionId + ' a'),
              tB = section.querySelectorAll('.tab-body-id-' + sectionId),
              tT = ev.target,
              tH = section.querySelector(ev.target.getAttribute('href'));
          if (tI) {
            tI.forEach(function(t) {
              t.classList.remove('first');
              t.setAttribute('tabindex','-1');
              t.removeAttribute('aria-selected');
            });
            tB.forEach(function(t) {
              t.classList.add('hide');
              var s = t.querySelector('.product-loop[data-glider="loaded"]');
              if (s) {
                s.setAttribute('data-glider', 'ignore');
              }
            });
          }
          tT.classList.add('first');
          tT.removeAttribute('tabindex');
          tT.setAttribute('aria-selected','true');      
          tH.classList.remove('hide');
          var loop = tH.querySelector('.product-loop[data-glider="unloaded"]');
          if (loop) {
            loop.setAttribute('data-glider', 'loaded');
            if (loop.getAttribute('data-glider') === 'loaded') {
              Sliders(section);
            }
          }
          var tC = document.getElementById('tabs-carousel-' + sectionId);
          if (tC) {        
            var mSI = parseInt(ev.target.getAttribute('data-gslide'));
            Glider(tC).scrollItem(parseInt(mSI));
          }
        };
      }
    } 
  }
  return Product;
})();

// FOOTER: End of File. 